import type { EventProps, EventsReport } from "../src/events-types";
import type { ProductDetail, ProductRow, ProductsReport } from "../src/products-types";
import type { Breakdown, BreakdownRow, ChartNote, DailyRow, EntryExit, FunnelReport, GoalDef, Journey, Overview, VisitEvent, VisitSummary, ProjectInfo, Sources, Totals, VitalKey, Vitals, Revenue, RevenueDimension } from "../src/types";
import { areaChart, deltaPill, sparkline } from "./chart";
import { countryMap } from "./map";
import { apiUrl, applySharedMode, initShare, shared } from "./share";
import { isOrg, onOrgPick, orgNote, orgOnlyNotice, orgSplit, orgSplitCard, projectOptions } from "./org";
import { renderErrors } from "./errors";
import { initAccount, type Me } from "./team";
import { startRealtime, stopRealtime } from "./realtime";
import { renderSettingsPage } from "./settings";
import { renderHome } from "./home";
import { onRetentionWeeks, renderRetention, renderReturning, retentionCard, returningCard } from "./retention";
import { renderSearch } from "./gsc";
import { renderUptime, uptimeCard } from "./uptime";
import { addNoteOn, currentNotes, fetchNotes, initNotes, renderNotes } from "./notes";
import { onSummaryRefresh, renderSummary, summaryCard, type SummaryOpts } from "./summary";
import { metricCardsSlot, renderMetricCards } from "./metric-cards";
import { renderInbox, startInboxBadge } from "./inbox";
import { openSettingsTab } from "./settings";
import { warmPush } from "./notify-settings";

/**
 * Ping on its own host (ping.monoblock.ae: the Worker marks the page with data-app="ping").
 * Only Ping and Settings (Ping, Account); "/" is Ping. README, "Ping".
 */
const PING = document.documentElement.dataset.app === "ping";

// ---------- state, kept in the URL so any view can be bookmarked or shared ----------

type Screen = "home" | "overview" | "traffic" | "performance" | "conversions" | "errors" | "realtime" | "settings" | "inbox";
const SHARED_HIDDEN = new Set<Screen>(["home", "conversions", "errors", "realtime", "settings", "inbox"]);
type Range = "today" | "7d" | "30d" | "90d";
type Metric = "visitors" | "visits" | "pageviews" | "bounce" | "duration" | "conversions";
type SourceTab = "channel" | "referrer" | "utm_campaign";
type Device = "all" | "mobile" | "desktop";
type GeoTab = "country" | "region" | "city" | "map";
const REV_DIMS: RevenueDimension[] = ["channel", "source", "referrer", "utm_source", "utm_medium", "utm_campaign", "entry_page", "country", "device"];

const FILTERABLE = [
  "channel", "source", "referrer", "utm_source", "utm_medium", "utm_campaign",
  "country", "region", "city", "device", "browser", "os", "page", "hostname", "language",
] as const;
type FilterKey = (typeof FILTERABLE)[number];

interface State {
  screen: Screen;
  project: string;
  range: Range;
  metric: Metric;
  src: SourceTab;
  device: Device;
  geo: GeoTab;
  /** Revenue by source: which dimension (Conversions). */
  rdim: RevenueDimension;
  filters: Partial<Record<FilterKey, string>>;
  /** Settings → Site settings: the site picked there (blank: follow the toolbar's project). */
  site: string;
}

const pick = <T extends string>(v: string | null, ok: readonly T[], d: T): T => (ok.includes(v as T) ? (v as T) : d);

const SCREENS = ["home", "overview", "traffic", "performance", "conversions", "errors", "realtime", "settings", "inbox"] as const;

/**
 * Where the URL puts us. Paths: `/` (Home), `/settings/`, `/project/<id>/` (Overview) and
 * `/project/<id>/<screen>/`; everything else (range, filters, …) stays in the query. Old links
 * (`/?screen=…&project=…`, still in sent emails) are read too, and writeUrl rewrites them.
 */
function readPath(q: URLSearchParams): { screen: string | null; project: string } {
  if (shared) return { screen: q.get("screen"), project: q.get("project") ?? "" };
  if (PING) return { screen: /^\/settings\/?$/.test(location.pathname) ? "settings" : "inbox", project: "" };
  const m = location.pathname.match(/^\/project\/([^/]+)(?:\/([a-z]+))?\/?$/);
  if (m) return { screen: m[2] ?? "overview", project: decodeURIComponent(m[1]!) };
  if (/^\/settings\/?$/.test(location.pathname)) return { screen: "settings", project: q.get("project") ?? "" };
  if (/^\/inbox\/?$/.test(location.pathname)) return { screen: "inbox", project: q.get("project") ?? "" };
  const project = q.get("project") ?? "";
  // A bare old-style project link (alert emails) meant that site, so open its Overview.
  return { screen: q.get("screen") ?? (project ? "overview" : "home"), project };
}

function readUrl(): State {
  const q = new URLSearchParams(location.search);
  const filters: State["filters"] = {};
  for (const k of FILTERABLE) if (q.get(k)) filters[k] = q.get(k)!;
  const at = readPath(q);
  return {
    screen: pick(at.screen, SCREENS, "home"),
    project: at.project,
    range: pick(q.get("range"), ["today", "7d", "30d", "90d"], "30d"),
    metric: pick(q.get("metric"), ["visitors", "visits", "pageviews", "bounce", "duration", "conversions"], "visitors"),
    src: pick(q.get("src"), ["channel", "referrer", "utm_campaign"], "channel"),
    device: pick(q.get("device"), ["all", "mobile", "desktop"], "all"),
    geo: pick(q.get("geo"), ["country", "region", "city", "map"], "country"),
    rdim: pick(q.get("rdim"), REV_DIMS, "channel"),
    filters,
    site: q.get("site") ?? "",
  };
}

function writeUrl(): void {
  const q = new URLSearchParams();
  let path = location.pathname; // a share link keeps its /s/<token> path and the old query form
  if (shared) {
    if (state.screen !== "overview") q.set("screen", state.screen);
    q.set("project", state.project);
  } else if (state.screen === "home") path = "/";
  else if (state.screen === "settings") path = "/settings/";
  else if (state.screen === "inbox") path = PING ? "/" : "/inbox/";
  else path = `/project/${encodeURIComponent(state.project)}/${state.screen === "overview" ? "" : `${state.screen}/`}`;
  // 30 days is the default, and Settings and the Inbox have no date range.
  if (state.range !== "30d" && state.screen !== "settings" && state.screen !== "inbox") q.set("range", state.range);
  if (state.metric !== "visitors") q.set("metric", state.metric);
  if (state.src !== "channel") q.set("src", state.src);
  if (state.device !== "all") q.set("device", state.device);
  if (state.geo !== "country") q.set("geo", state.geo);
  if (state.rdim !== "channel") q.set("rdim", state.rdim);
  for (const [k, v] of Object.entries(state.filters)) q.set(k, v);
  if (state.screen === "settings" && state.site) q.set("site", state.site);
  const qs = q.toString();
  history.replaceState(null, "", qs ? `${path}?${qs}` : path);
}

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const screen = $("screen");
let projects: ProjectInfo[] = [];
let state = readUrl();
let generation = 0; // drops responses from superseded requests
let csv: Record<string, { name: string; rows: () => Record<string, unknown>[] }> = {};
/** A notification to open on the Inbox: /inbox/?n=<id> (a push's link), read before writeUrl drops it. */
let inboxOpen: string | null = new URLSearchParams(location.search).get("n");

// ---------- dates ----------

const iso = (d: Date) => d.toISOString().slice(0, 10);
const fromIso = (s: string) => new Date(`${s}T00:00:00Z`);
const shift = (s: string, days: number) => iso(new Date(fromIso(s).getTime() + days * 86_400_000));
const DAYS: Record<Range, number> = { today: 1, "7d": 7, "30d": 30, "90d": 90 };
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayLabel = (s: string) => `${Number(s.slice(8, 10))} ${MON[Number(s.slice(5, 7)) - 1]}`;

function project(): ProjectInfo {
  return projects.find((p) => p.id === state.project)!;
}

/** The range against the project's own "today", not the browser's. */
function params(extra: Record<string, string> = {}): Record<string, string> {
  const today = project().today;
  return {
    project: state.project,
    from: shift(today, -(DAYS[state.range] - 1)),
    to: today,
    ...(state.filters as Record<string, string>),
    ...extra,
  };
}

// ---------- formatting (handoff rules) ----------

const fmtN = (n: number) => (n >= 10_000 ? `${(n / 1000).toFixed(1)}k` : Math.round(n).toLocaleString("en-US"));
const fmtDur = (sec: number) => {
  const s = Math.round(sec);
  return s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;
};
const fmtPct = (r: number | null | undefined) => (r === null || r === undefined ? "–" : `${Math.round(r * 100)}%`);
const fmtPct1 = (r: number | null | undefined) => (r === null || r === undefined ? "–" : `${(r * 100).toFixed(1)}%`);
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const moneyFormats = new Map<string, Intl.NumberFormat>();
/** "AED 12,340" (whole units from 100 up), "AED 4.50" below. One currency per project; never converted. */
const fmtMoney = (n: number | null | undefined, currency: string) => {
  if (n === null || n === undefined || !Number.isFinite(n)) return "–";
  const digits = Math.abs(n) >= 100 || Number.isInteger(n) ? 0 : 2;
  const key = `${currency}|${digits}`;
  let f = moneyFormats.get(key);
  if (!f) {
    try {
      f = new Intl.NumberFormat("en-US", { style: "currency", currency, currencyDisplay: "code", minimumFractionDigits: digits, maximumFractionDigits: digits });
    } catch {
      f = new Intl.NumberFormat("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
    }
    moneyFormats.set(key, f);
  }
  return f.format(n).replace(/\u00a0/g, " ");
};
const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();
const countryName = (code: string) => (code ? (regionNames?.of(code) ?? code) : "Unknown");
const languageNames = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "language" });
  } catch {
    return null;
  }
})();
/** "en" -> "English", "ar" -> "Arabic"; the code itself if the browser doesn't know it. */
const languageName = (code: string) => {
  if (!code) return "Unknown";
  try {
    return languageNames?.of(code) ?? code;
  } catch {
    return code;
  }
};
/** "1 session", "2 sessions". */
const plural = (n: number, word: string) => `${fmtN(n)} ${word}${Math.round(n) === 1 ? "" : "s"}`;
const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

function label(dim: string, key: string): string {
  if (key === "") return ["country", "region", "city", "language"].includes(dim) ? "Unknown" : "(none)";
  if (dim === "country") return countryName(key);
  if (dim === "language") return languageName(key);
  if (dim === "device") return cap(key);
  return key;
}

// ---------- API ----------

async function api<T>(path: string, p: Record<string, string> = {}): Promise<T> {
  const res = await fetch(`${apiUrl(path)}?${new URLSearchParams(p)}`, { credentials: "same-origin" });
  const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  // Client logins: a missing or expired session goes to the sign-in page.
  if (res.status === 401 && (body as { logins?: boolean }).logins) location.href = "/login";
  if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  return body as T;
}

const ERRORS: Record<string, string> = {
  visits_only_last_90_days: "Individual visits are kept for the last 90 days.",
  hot_store_not_configured: "The Analytics Engine API token is not set, so recent data can't be read. Set AE_API_TOKEN on the dashboard Worker (see README).",
  filters_unavailable_beyond_hot_window: "Filters only work for the last 90 days. Older data comes from daily summaries, which can't be filtered.",
  not_signed_in: "You're not signed in. Reload the page to sign in.",
  not_allowed: "This account isn't allowed to view the dashboard.",
  read_only: "You have view-only access to this project. Ask an owner to make changes.",
};
/** Shows an error for an action without losing the screen. */
function alertCard(err: unknown): void {
  screen.insertAdjacentHTML("afterbegin", errorCard(err));
}
const errorCard = (err: unknown) => `<div class="notice error">${esc(ERRORS[(err as Error).message] ?? `Couldn't load data: ${(err as Error).message}`)}</div>`;

// ---------- CSV ----------

function downloadCsv(key: string): void {
  const src = csv[key];
  const rows = src?.rows() ?? [];
  if (!rows.length) return;
  const cols = Object.keys(rows[0]!);
  const cell = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [cols.join(","), ...rows.map((r) => cols.map((c) => cell(r[c])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  a.download = `${state.project}-${src!.name}-${params().from}_${params().to}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
const exportLink = (key: string) => `<button class="link" data-csv="${key}">Export CSV</button>`;

// ---------- shared pieces ----------

/** `slot` names a card that an in-card toggle re-renders on its own (see updateCard). */
const card = (title: string, body: string, opts: { csvKey?: string; extra?: string; cls?: string; slot?: string } = {}) =>
  `<section class="card ${opts.cls ?? ""}"${opts.slot ? ` data-card="${opts.slot}"` : ""}><div class="card-head"><h2>${title}</h2>${opts.extra ?? ""}<span class="spacer"></span>${opts.csvKey ? exportLink(opts.csvKey) : ""}</div>${body}</section>`;

const seg = (attr: string, items: [string, string][], cur: string, small = true) =>
  `<div class="seg ${small ? "small" : ""}" role="group">${items.map(([l, v]) => `<button data-${attr}="${v}" aria-pressed="${v === cur}">${l}</button>`).join("")}</div>`;

const skeleton = (h: number) => `<div class="skeleton" style="height:${h}px"></div>`;

// ---------- card-local updates: an in-card toggle re-renders only its own card ----------

/** The generation whose screen has finished drawing; toggles clicked before that do a full refresh. */
let settled = 0;
/** Per-card tokens, so a slow response for an earlier click never overwrites a later one. */
const cardTokens: Record<string, number> = {};
const slotOf = (key: string) => document.querySelector<HTMLElement>(`[data-card="${key}"]`);

/** Takes the card's next token; the result says whether that render is still the one wanted. */
function claim(key: string, gen = generation): () => boolean {
  const tok = (cardTokens[key] = (cardTokens[key] ?? 0) + 1);
  return () => gen === generation && cardTokens[key] === tok && !!slotOf(key);
}

/** Presses the clicked button of a segmented control at once, before its card's data arrives. */
function press(btn: HTMLElement | null): void {
  const group = btn?.parentElement;
  if (group) for (const b of group.querySelectorAll<HTMLElement>(":scope > button")) b.setAttribute("aria-pressed", String(b === btn));
}

/** Replaces a card's markup in place, keeping the page where it was. */
function swapCard(key: string, html: string): HTMLElement | null {
  const el = slotOf(key);
  if (!el) return null;
  const y = scrollY;
  el.outerHTML = html;
  if (scrollY !== y) scrollTo(scrollX, y);
  return slotOf(key);
}

/**
 * Re-renders one card after an in-card toggle: the card dims while `run` fetches and swaps it,
 * the rest of the screen is left alone. Falls back to a full refresh while the screen is still
 * drawing (its own render would otherwise overwrite the card).
 */
function updateCard(key: string, run: (current: () => boolean) => Promise<void> | void): void {
  writeUrl();
  const el = slotOf(key);
  if (!el || settled !== generation) return void refresh();
  const current = claim(key);
  el.classList.add("loading");
  void (async () => {
    try {
      await run(current);
      if (current()) slotOf(key)!.classList.remove("loading");
    } catch (err) {
      if (!current()) return;
      const host = slotOf(key)!;
      host.classList.remove("loading");
      host.querySelector(":scope > .notice.error")?.remove();
      host.insertAdjacentHTML("beforeend", errorCard(err));
    }
  })();
}

interface Col<R> {
  head: string;
  width: number;
  value: (r: R) => string;
  primary?: boolean;
}

/**
 * The handoff's ranked bar-row table. `bar` is the value the bar is scaled by (relative to the top
 * row); `name` renders the label; rows can filter the dashboard when clicked.
 */
function rankTable<R>(
  rows: R[],
  o: { head: string; name: (r: R) => string; bar: (r: R) => number; cols: Col<R>[]; warm?: boolean; filter?: (r: R) => [FilterKey, string] | null; cls?: string },
): string {
  if (!rows.length) return `<div class="empty">No data for this range yet.</div>`;
  const grid = `grid-template-columns:minmax(0,1fr) ${o.cols.map((c) => `${c.width}px`).join(" ")}`;
  const max = Math.max(1, ...rows.map(o.bar));
  const head = `<div class="rhead" style="${grid}"><span>${o.head}</span>${o.cols.map((c) => `<span>${c.head}</span>`).join("")}</div>`;
  const body = rows
    .map((r) => {
      const f = o.filter?.(r);
      const click = f && !state.filters[f[0]] ? ` clickable" data-filter="${f[0]}" data-value="${esc(f[1])}" title="Filter by this` : "";
      return `<div class="row${click}" style="${grid}"><div class="barcell ${o.warm ? "warm" : ""}"><div class="bar" style="width:${((o.bar(r) / max) * 100).toFixed(1)}%"></div>${o.name(r)}</div>${o.cols
        .map((c) => `<span class="n ${c.primary ? "p" : ""}">${c.value(r)}</span>`)
        .join("")}</div>`;
    })
    .join("");
  return `<div class="rows ${o.cls ?? ""}">${head}${body}</div>`;
}
const textLabel = (main: string, sub = "") => `<span class="lbl">${esc(main)}${sub ? `<span class="sub">${esc(sub)}</span>` : ""}</span>`;
const pathLabel = (p: string) => `<span class="path" title="${esc(p)}">${esc(p || "(none)")}</span>`;

// ---------- Overview ----------

const METRICS: { key: Metric; label: string; invert?: boolean }[] = [
  { key: "visitors", label: "Visitors" },
  { key: "visits", label: "Visits" },
  { key: "pageviews", label: "Pageviews" },
  { key: "bounce", label: "Bounce rate", invert: true },
  { key: "duration", label: "Visit length" },
  { key: "conversions", label: "Conversions" },
];

function dayValue(m: Metric, d: DailyRow): number | null {
  switch (m) {
    case "visitors": return d.visitors;
    case "visits": return d.sessions;
    case "pageviews": return d.pageviews;
    case "bounce": return d.bounce_rate === null ? null : d.bounce_rate * 100;
    case "duration": return d.avg_duration_ms === null ? null : d.avg_duration_ms / 1000;
    case "conversions": return d.conversions;
  }
}
function totalValue(m: Metric, t: Overview["previous"] | Totals): number | null {
  if (!t) return null;
  switch (m) {
    case "visitors": return t.visitors;
    case "visits": return t.sessions;
    case "pageviews": return t.pageviews;
    case "bounce": return t.bounce_rate === null ? null : t.bounce_rate * 100;
    case "duration": return t.avg_duration_ms === null ? null : t.avg_duration_ms / 1000;
    case "conversions": return t.conversions;
  }
}
const fmtMetric = (m: Metric, v: number | null) => (v === null ? "–" : m === "bounce" ? `${Math.round(v)}%` : m === "duration" ? fmtDur(v) : fmtN(v));

/** Admins, and owners of this project, add and edit chart notes; never on share links or org views. */
const canEditNotes = () => !shared && project().kind !== "org" && (me?.role === "admin" || !!me?.can_edit?.includes(state.project));

const metricDef = () => METRICS.find((m) => m.key === state.metric)!;

/** The metric buttons over the Overview chart (desktop: with sparklines). */
function kpisHtml(o: Overview, mobile: boolean): string {
  return METRICS.map((m) => {
    const cur = totalValue(m.key, o.totals);
    const sel = m.key === state.metric;
    if (mobile)
      return `<button class="m-kpi" data-metric="${m.key}" aria-pressed="${sel}"><span class="label">${m.label}</span>
      <span class="vr"><span class="v num">${fmtMetric(m.key, cur)}</span>${deltaPill(cur, totalValue(m.key, o.previous), m.invert)}</span></button>`;
    return `<button class="kpi" data-metric="${m.key}" aria-pressed="${sel}">
      <span class="label">${m.label}</span>
      <span class="value num">${fmtMetric(m.key, cur)}</span>
      <span class="foot">${deltaPill(cur, totalValue(m.key, o.previous), m.invert)}${o.daily.some((d) => dayValue(m.key, d) !== null) ? sparkline(o.daily.map((d) => dayValue(m.key, d)), sel ? "#2F5BD3" : "#A9B0BF") : ""}</span>
    </button>`;
  }).join("");
}

/** The Overview data on screen, so switching the chart's metric redraws it without a fetch. */
let overviewShown: { o: Overview; mobile: boolean; draw: () => void } | null = null;

/** Metric buttons: new KPI states, chart title and chart; nothing else on the screen changes. */
function switchMetric(m: Metric): void {
  state.metric = m;
  const kpis = document.getElementById("kpis");
  const title = document.getElementById("chart-title");
  if (!overviewShown || settled !== generation || overviewShown.mobile !== mq.matches || !kpis || !title) return void refresh();
  writeUrl();
  kpis.innerHTML = kpisHtml(overviewShown.o, overviewShown.mobile);
  title.textContent = `${metricDef().label} per day`;
  overviewShown.draw();
}

/** The Overview chart with its notes (markers, plus the list under it), redrawn when notes change. */
function overviewChart(o: Overview, notes: ChartNote[], gen: number, compact: boolean): void {
  const days = o.daily.map((d) => d.day);
  const draw = () => {
    if (gen !== generation || !document.getElementById("chart")) return;
    areaChart($("chart"), {
      labels: o.daily.map((d) => dayLabel(d.day)),
      current: o.daily.map((d) => dayValue(state.metric, d)),
      previous: o.previous_daily.length ? o.previous_daily.map((d) => dayValue(state.metric, d)) : undefined,
      metric: metricDef().label,
      format: (v) => fmtMetric(state.metric, v),
      tick: (v) => fmtMetric(state.metric, v).replace(/m 00s$/, "m"),
      compact,
      days,
      notes: currentNotes(),
      onPickDay: canEditNotes() ? addNoteOn : undefined,
    });
    renderNotes();
  };
  initNotes({ project: state.project, from: days[0] ?? o.range.from, to: days[days.length - 1] ?? o.range.to, canEdit: canEditNotes(), redraw: draw, current: () => gen === generation }, notes);
  overviewShown = { o, mobile: compact, draw };
  draw();
}

/**
 * Overview narrowed to one page (a page filter): who visited it, by place and device, under the
 * chart. The same breakdowns as Traffic, with every filter applied.
 */
async function renderPageDetail(gen: number): Promise<void> {
  const el = document.getElementById("page-detail");
  if (!el || !state.filters.page) return;
  const mobile = mq.matches;
  el.innerHTML = mobile ? `${mcard("Countries", mskeleton(4, 44))}` : `<div class="row2">${card(geoTitle(), skeleton(220))}${card("Devices", skeleton(220))}</div>`;
  let geo: Breakdown, dev: Breakdown, br: Breakdown, os: Breakdown | null;
  try {
    [geo, dev, br, os] = await Promise.all([
      mobile ? api<Breakdown>("breakdown", params({ dim: "country", limit: "8" })) : fetchPageGeo(),
      api<Breakdown>("breakdown", params({ dim: "device", limit: "10" })),
      api<Breakdown>("breakdown", params({ dim: "browser", limit: mobile ? "20" : "6" })),
      mobile ? Promise.resolve(null) : api<Breakdown>("breakdown", params({ dim: "os", limit: "6" })),
    ]);
  } catch (err) {
    if (gen === generation) el.innerHTML = errorCard(err);
    return;
  }
  if (gen !== generation) return;
  const devRows = dev.rows.filter((r) => r.visitors > 0);
  const devTotal = devRows.reduce((t, r) => t + r.visitors, 0) || 1;
  const bar = `<div class="stackbar" role="img" aria-label="Visitors by device">${devRows.map((d) => `<div style="width:${(d.visitors / devTotal) * 100}%;background:${DEVICE_COLORS[d.key] ?? "#CBD2DF"}"></div>`).join("")}</div>`;
  const legend = (cls: string) => `<div class="${cls}">${devRows.map((d) => `<div><span class="k"><i style="background:${DEVICE_COLORS[d.key] ?? "#CBD2DF"}"></i>${esc(label("device", d.key))}</span><b class="num">${Math.round((d.visitors / devTotal) * 100)}%</b></div>`).join("")}</div>`;
  const empty = `<div class="empty">No data for this range yet.</div>`;
  if (mobile) {
    el.innerHTML = `${mcard("Countries", mBars(geo.rows, {
      name: (c) => `<span class="l center"><span class="iso">${esc(c.key || "??")}</span>${esc(countryName(c.key))}</span>`,
      value: (c) => fmtN(c.visitors), bar: (c) => c.visitors, filter: (c) => ["country", c.key],
    }), "Visitors")}
    ${mcard("Devices", devRows.length ? `<div class="m-dev">${bar}${legend("legend3")}</div>` : empty)}`;
    return;
  }
  const devices = devRows.length
    ? `<div class="devices"><div style="display:flex;flex-direction:column;gap:12px">${bar}${legend("dev-legend")}</div>
      <div class="two">${shareList("BROWSER", br.rows)}${shareList("OPERATING SYSTEM", os!.rows)}</div></div>`
    : empty;
  el.innerHTML = `<div class="row2">${pageGeoCard(geo)}${card("Devices", devices)}</div>`;
}

/** Page detail's place breakdown has no map: "map" (kept from Traffic) shows countries. */
const pageGeoDim = (): "country" | "region" | "city" => (state.geo === "map" ? "country" : state.geo);
const fetchPageGeo = () => api<Breakdown>("breakdown", params({ dim: pageGeoDim(), limit: "8" }));
const pageGeoCard = (geo: Breakdown) =>
  card(geoTitle(), geoTable(geo, pageGeoDim()), { extra: seg("geo", GEO_TABS.filter(([, v]) => v !== "map"), pageGeoDim()), slot: "page-geo" });

async function renderOverview(gen: number): Promise<void> {
  screen.innerHTML = `<section class="card flush">${skeleton(110)}<div class="chart-wrap">${skeleton(300)}</div></section><div class="row2">${card("Channels", skeleton(240))}${card("Top pages", skeleton(240))}</div>`;
  // "Today" draws yesterday too, so the chart has a line rather than a dot.
  const extra: Record<string, string> = state.range === "today" ? { chart_from: shift(project().today, -1) } : {};
  const [o, notes] = await Promise.all([api<Overview>("overview", params(extra)), fetchNotes(state.project, extra.chart_from ?? params().from!, params().to!)]);
  if (gen !== generation) return;

  const M = metricDef();
  const first = o.daily[0]?.day ?? o.range.from;
  const range = `${dayLabel(first)} – ${dayLabel(o.range.to)}`;
  const note = o.source === "rollup"
    ? `<div class="notice">This range is older than the 90-day live store, so it's read from daily summaries. "Visitors" adds up each day's visitors, and visit length isn't available.</div>`
    : "";

  const chTotal = o.channels.reduce((s, c) => s + c.visitors, 0);
  const goals = o.goals.length
    ? `<div class="goals">${o.goals
        .map((g) => {
          const r = g.rate ?? 0;
          return `<div class="goal">
          <div class="t"><span>${esc(g.name)}</span>${deltaPill(g.rate, g.prev_rate)}</div>
          <div class="rate"><b class="num">${fmtPct1(g.rate)}</b><span>conversion rate</span></div>
          <div class="progress" role="img" aria-label="${fmtPct1(g.rate)} conversion rate"><div style="width:${Math.min(100, r * 1000).toFixed(1)}%"></div></div>
          <div class="f"><span><b class="num">${fmtN(g.completions)}</b> completions</span><span><b class="num">${fmtN(g.visitors)}</b> visitors</span></div>
        </div>`;
        })
        .join("")}</div>`
    : `<div class="empty">No goals configured for this project yet.</div>`;

  screen.innerHTML = `${orgNote(project())}${note}${summaryCard(!isOrg(project()))}
  <section class="card flush">
    <div class="kpis" id="kpis" role="group" aria-label="Chart metric">${kpisHtml(o, false)}</div>
    <div class="chart-wrap">
      <div class="chart-head"><h2 id="chart-title">${M.label} per day</h2><span class="spacer"></span>
        <div class="legend"><span><i class="cur"></i>${range}</span>${o.previous_daily.length ? `<span><i class="prev"></i>Previous period</span>` : ""}${exportLink("daily")}</div>
      </div>
      <div id="chart"></div>
      <div id="notes" class="notes"></div>
    </div>
  </section>
  ${metricCardsSlot()}
  <div class="row2">
    ${card("Channels", rankTable(o.channels, {
      head: "Source",
      name: (c) => textLabel(c.key || "(none)", chTotal ? `${Math.round((c.visitors / chTotal) * 100)}%` : ""),
      bar: (c) => c.visitors,
      cols: [{ head: "Visitors", width: 72, value: (c) => fmtN(c.visitors), primary: true }, { head: "Sessions", width: 72, value: (c) => fmtN(c.sessions) }],
      filter: (c) => ["channel", c.key],
    }), { csvKey: "channels" })}
    ${card("Top pages", rankTable(o.pages, {
      head: "Page",
      name: (p) => pathLabel(p.key),
      bar: (p) => p.pageviews,
      warm: true,
      cols: [{ head: "Pageviews", width: 80, value: (p) => fmtN(p.pageviews), primary: true }, { head: "Visitors", width: 72, value: (p) => fmtN(p.visitors) }],
      filter: (p) => ["page", p.key],
    }), { csvKey: "pages" })}
  </div>
  ${state.filters.page ? `<div id="page-detail"></div>` : ""}
  ${returningCard(false)}
  ${isOrg(project()) ? "" : uptimeCard()}
  ${card("Goals", goals, { csvKey: "goals", extra: `<span class="spacer"></span><button class="link" data-tab="conversions">Goals & funnels</button>` })}`;

  settled = gen;
  overviewChart(o, notes, gen, false);
  void renderMetricCards(metricOpts(gen, false));
  void renderReturning(retOpts(gen));
  void renderUptime(uptimeOpts(gen));
  void renderPageDetail(gen);
  void renderSummary(summaryOpts(gen));

  if (o.live !== null) {
    $("live").hidden = false;
    $("live-text").textContent = `${fmtN(o.live)} ${o.live === 1 ? "visitor" : "visitors"} online now`;
  }

  csv = {
    daily: { name: "daily", rows: () => o.daily.map((d, i) => ({ ...d, previous_day: o.previous_daily[i]?.day ?? "", previous_visitors: o.previous_daily[i]?.visitors ?? "" })) },
    channels: { name: "channels", rows: () => o.channels as unknown as Record<string, unknown>[] },
    pages: { name: "pages", rows: () => o.pages as unknown as Record<string, unknown>[] },
    goals: { name: "goals", rows: () => o.goals.map(({ name, completions, visitors, rate }) => ({ goal: name, completions, visitors, conversion_rate: rate })) },
  };
}

// ---------- Traffic ----------

const DEVICE_COLORS: Record<string, string> = { desktop: "#16233D", mobile: "#2F5BD3", tablet: "#A9B8E6" };

function shareList(title: string, rows: BreakdownRow[]): string {
  const total = rows.reduce((s, r) => s + r.visitors, 0) || 1;
  const top = rows.slice(0, 4);
  const other = rows.slice(4).reduce((s, r) => s + r.visitors, 0);
  const items = [...top.map((r) => [r.key || "Other", r.visitors] as const), ...(other ? [["Other", other] as const] : [])];
  if (!items.length) return `<div class="share-list"><span class="h">${title}</span><span class="empty">No data yet.</span></div>`;
  return `<div class="share-list"><span class="h">${title}</span>${items
    .map(([n, v]) => {
      const p = `${Math.round((v / total) * 100)}%`;
      return `<div class="share-item"><div class="l"><span>${esc(n)}</span><span>${p}</span></div><div class="t"><div style="width:${p}"></div></div></div>`;
    })
    .join("")}</div>`;
}

const GEO_TABS: [string, string][] = [["Countries", "country"], ["Regions", "region"], ["Cities", "city"], ["Map", "map"]];
const SRC_TABS: [string, string][] = [["Channels", "channel"], ["Referrers", "referrer"], ["Campaigns", "utm_campaign"]];
const geoTitle = () => ({ country: "Countries", region: "Regions", city: "Cities", map: "Countries" })[state.geo];
const GEO_HEAD = { country: "Country", region: "Region", city: "City" } as const;

/** A place breakdown as a ranked table (Traffic, and Overview's page detail). */
function geoTable(geo: Breakdown, dim: "country" | "region" | "city"): string {
  return rankTable(geo.rows, {
    head: GEO_HEAD[dim],
    name: (c) =>
      dim === "country" ? `<span class="lbl center"><span class="iso">${esc(c.key || "??")}</span>${esc(countryName(c.key))}</span>` : textLabel(label(dim, c.key)),
    bar: (c) => c.visitors,
    cols: [
      { head: "Visitors", width: 72, value: (c) => fmtN(c.visitors), primary: true },
      { head: "Share", width: 56, value: (c) => (geo.total_visitors ? `${Math.round((c.visitors / geo.total_visitors) * 100)}%` : "–") },
    ],
    filter: (c) => [dim, c.key],
  });
}

// Traffic's place card: Countries / Regions / Cities / Map.
const trafficGeoDim = (): "country" | "region" | "city" => (state.geo === "map" ? "country" : state.geo);
const fetchGeo = () => api<Breakdown>("breakdown", params({ dim: trafficGeoDim(), limit: state.geo === "map" ? "250" : "8" }));
/** The card; `null` draws it loading. */
const geoCard = (geo: Breakdown | null) =>
  card(geoTitle(), geo ? (state.geo === "map" ? `<div class="map" id="map"></div>` : geoTable(geo, trafficGeoDim())) : skeleton(260), {
    csvKey: geo ? "geo" : undefined,
    extra: seg("geo", GEO_TABS, state.geo),
    slot: "geo",
  });
/** Once the card is on screen: its CSV export, and the map when that tab is picked. */
async function geoAfter(geo: Breakdown): Promise<void> {
  csv.geo = { name: trafficGeoDim(), rows: () => geo.rows as unknown as Record<string, unknown>[] };
  if (state.geo === "map")
    await countryMap($("map"), new Map(geo.rows.map((r) => [r.key, r.visitors])), {
      tooltip: $("maptip"),
      format: fmtN,
      name: countryName,
      onPick: (c) => (state.filters.country ? undefined : setFilter("country", c)),
    });
}

// Traffic's Sources card: Channels / Referrers / Campaigns.
const fetchSources = () => api<Sources>("sources", params({ dim: state.src }));
const sourcesCsv = (src: Sources) => ({
  name: state.src,
  rows: () => src.rows.map(({ revenue, per_visitor, ...r }) => (src.currency ? { ...r, currency: src.currency, revenue, revenue_per_visitor: per_visitor } : r)),
});
/** The desktop card; `null` draws it loading. */
const sourcesCard = (src: Sources | null) =>
  card("Sources", src ? `<div class="table-scroll">${sourcesTable(src)}</div>` : skeleton(260), { csvKey: src ? "sources" : undefined, extra: seg("src", SRC_TABS, state.src), slot: "src" });

function sourcesTable(src: Sources): string {
  const head = { channel: "Channel", referrer: "Referrer", utm_campaign: "Campaign" }[state.src];
  const fkey: FilterKey = state.src;
  // Revenue columns only when the project has a goal carrying a value (the API then sends its currency).
  const cur = src.currency;
  return rankTable(src.rows, {
    head,
    name: (s) => textLabel(s.key || "(none)", s.sub),
    bar: (s) => s.visitors,
    cls: cur ? "wide xwide" : "wide",
    cols: [
      { head: "Visitors", width: 84, value: (s) => fmtN(s.visitors), primary: true },
      { head: "Sessions", width: 84, value: (s) => fmtN(s.sessions) },
      { head: "Bounce", width: 84, value: (s) => fmtPct(s.bounce_rate) },
      { head: "Conv. rate", width: 96, value: (s) => fmtPct1(s.conv_rate) },
      ...(cur
        ? [
            { head: "Revenue", width: 112, value: (s: Sources["rows"][number]) => fmtMoney(s.revenue, cur) },
            { head: "Per visitor", width: 96, value: (s: Sources["rows"][number]) => fmtMoney(s.per_visitor, cur) },
          ]
        : []),
    ],
    filter: (s) => [fkey, s.key],
  });
}

async function renderTraffic(gen: number): Promise<void> {
  screen.innerHTML = `${orgSplitCard(project())}${sourcesCard(null)}
  <div class="row2">${geoCard(null)}${card("Devices", skeleton(260))}</div>
  ${card("Languages", skeleton(200))}
  <div class="row2">${card("Entry pages", skeleton(200))}${card("Exit pages", skeleton(200))}</div>`;
  const [src, geo, dev, br, os, ee, lang] = await Promise.all([
    fetchSources(),
    fetchGeo(),
    api<Breakdown>("breakdown", params({ dim: "device", limit: "10" })),
    api<Breakdown>("breakdown", params({ dim: "browser", limit: "20" })),
    api<Breakdown>("breakdown", params({ dim: "os", limit: "20" })),
    api<EntryExit>("entry_exit", params()),
    api<Breakdown>("breakdown", params({ dim: "language", limit: "8" })),
  ]);
  if (gen !== generation) return;

  const languages = rankTable(lang.rows, {
    head: "Language",
    name: (l) => textLabel(languageName(l.key), l.key),
    bar: (l) => l.visitors,
    cols: [
      { head: "Visitors", width: 72, value: (l) => fmtN(l.visitors), primary: true },
      { head: "Share", width: 56, value: (l) => (lang.total_visitors ? `${Math.round((l.visitors / lang.total_visitors) * 100)}%` : "–") },
    ],
    filter: (l) => (l.key ? ["language", l.key] : null),
  });

  const devRows = dev.rows.filter((r) => r.visitors > 0);
  const devTotal = devRows.reduce((s, r) => s + r.visitors, 0) || 1;
  const devices = devRows.length
    ? `<div class="devices"><div style="display:flex;flex-direction:column;gap:12px">
        <div class="stackbar" role="img" aria-label="Visitors by device">${devRows.map((d) => `<div style="width:${(d.visitors / devTotal) * 100}%;background:${DEVICE_COLORS[d.key] ?? "#CBD2DF"}"></div>`).join("")}</div>
        <div class="dev-legend">${devRows.map((d) => `<div><span class="k"><i style="background:${DEVICE_COLORS[d.key] ?? "#CBD2DF"}"></i>${esc(label("device", d.key))}</span><b class="num">${Math.round((d.visitors / devTotal) * 100)}%</b></div>`).join("")}</div>
      </div>
      <div class="two">${shareList("BROWSER", br.rows)}${shareList("OPERATING SYSTEM", os.rows)}</div></div>`
    : `<div class="empty">No data for this range yet.</div>`;

  const entry = rankTable(ee.entry, {
    head: "Page", name: (p) => pathLabel(p.key), bar: (p) => p.sessions, warm: true,
    cols: [{ head: "Visits", width: 72, value: (p) => fmtN(p.sessions), primary: true }, { head: "Bounce", width: 72, value: (p) => fmtPct(p.bounce_rate) }],
    filter: (p) => ["page", p.key],
  });
  const exit = rankTable(ee.exit, {
    head: "Page", name: (p) => pathLabel(p.key), bar: (p) => p.exits, warm: true,
    cols: [{ head: "Exits", width: 72, value: (p) => fmtN(p.exits), primary: true }, { head: "Exit rate", width: 72, value: (p) => fmtPct(p.exit_rate) }],
    filter: (p) => ["page", p.key],
  });

  screen.innerHTML = `${src.source === "rollup" ? `<div class="notice">This range comes from daily summaries, which don't keep bounce or conversion rates per source.</div>` : ""}
  ${orgSplitCard(project())}
  ${sourcesCard(src)}
  <div class="row2">${geoCard(geo)}${card("Devices", devices, { csvKey: "devices" })}</div>
  ${card("Languages", languages, { csvKey: "languages" })}
  <div class="row2">${card("Entry pages", entry, { csvKey: "entry" })}${card("Exit pages", exit, { csvKey: "exit" })}</div>
  ${retentionCard(false)}
  <div id="gsc" hidden></div>
  ${card("Page flow", `<div id="flow">${skeleton(220)}</div>`)}
  ${card("Recent visits", `<div id="visits">${skeleton(200)}</div>`)}`;
  settled = gen;
  void renderFlow(gen, false);
  void renderRetention(retOpts(gen));
  void renderVisits(gen);
  void renderSearch(document.getElementById("gsc"), { project: project(), params: params(), mobile: false, current: () => gen === generation });
  void orgSplit(document.getElementById("org-split"), params(), () => gen !== generation);

  csv = {
    sources: sourcesCsv(src),
    devices: { name: "devices", rows: () => [...dev.rows.map((r) => ({ kind: "device", ...r })), ...br.rows.map((r) => ({ kind: "browser", ...r })), ...os.rows.map((r) => ({ kind: "os", ...r }))] },
    languages: { name: "languages", rows: () => lang.rows.map((r) => ({ language: languageName(r.key), ...r })) },
    entry: { name: "entry-pages", rows: () => ee.entry as unknown as Record<string, unknown>[] },
    exit: { name: "exit-pages", rows: () => ee.exit as unknown as Record<string, unknown>[] },
  };
  await geoAfter(geo);
}

// ---------- Performance ----------

const VITALS: { key: VitalKey; abbr: string; name: string; good: number; poor: number; thr: string }[] = [
  { key: "lcp", abbr: "LCP", name: "Largest Contentful Paint", good: 2500, poor: 4000, thr: "Good ≤ 2.5s · Poor > 4s" },
  { key: "inp", abbr: "INP", name: "Interaction to Next Paint", good: 200, poor: 500, thr: "Good ≤ 200ms · Poor > 500ms" },
  { key: "cls", abbr: "CLS", name: "Cumulative Layout Shift", good: 0.1, poor: 0.25, thr: "Good ≤ 0.10 · Poor > 0.25" },
  { key: "ttfb", abbr: "TTFB", name: "Time to First Byte", good: 800, poor: 1800, thr: "Good ≤ 0.8s · Poor > 1.8s" },
];
const RATING = { good: "Good", ni: "Needs improvement", poor: "Poor" } as const;
const rateVital = (k: VitalKey, v: number): keyof typeof RATING => {
  const t = VITALS.find((x) => x.key === k)!;
  return v <= t.good ? "good" : v <= t.poor ? "ni" : "poor";
};
/** Below this many page loads a p75 swings with every visit, so the screen says it's a small sample. */
const LOW_SAMPLE = 50;
const lowSample = (n: number | undefined) => (n && n < LOW_SAMPLE ? `<span class="low">Low sample: ${plural(n, "page load")}</span>` : "");
const lowSampleNotice = (loads: number) =>
  loads > 0 && loads < LOW_SAMPLE
    ? `<div class="notice low-sample">Only ${plural(loads, "page load")} measured in this range, so these figures can shift a lot as more come in.</div>`
    : "";
const fmtVital = (k: VitalKey, v: number) => (k === "lcp" || k === "ttfb" ? `${(v / 1000).toFixed(1)}s` : k === "inp" ? `${Math.round(v)}ms` : v.toFixed(2));

// Performance: the device toggle changes everything under it, so the "card" it re-renders is the
// vitals, the notice and the Pages table (a display:contents slot), plus the title's subtitle.
const fetchVitals = () => api<Vitals>("vitals", params(state.device !== "all" ? { dim: "page", device: state.device } : { dim: "page" }));
const vitalLoads = (v: Vitals) => Math.max(0, ...VITALS.map((x) => v.overall[x.key]?.samples ?? 0));
const vitalsCsv = (v: Vitals) => ({
  name: "web-vitals",
  rows: () => v.rows.map((r) => ({ page: r.key, ...Object.fromEntries(VITALS.map((x) => [`${x.key}_p75`, r[x.key]?.p75 ?? ""])) })),
});
/** The desktop title block; without data (loading) it has no subtitle. */
const perfTitle = (v: Vitals | null) =>
  v
    ? `<div style="display:flex;flex-direction:column;gap:4px"><h1>Core Web Vitals</h1><span class="s">75th percentile from ${plural(vitalLoads(v), "real page load")}${v.approximate ? " · older than 90 days, so p75 is estimated from daily figures" : ""}</span></div>`
    : `<div><h1>Core Web Vitals</h1></div>`;

async function renderPerformance(gen: number): Promise<void> {
  const devSeg = seg("device", [["All devices", "all"], ["Mobile", "mobile"], ["Desktop", "desktop"]], state.device);
  const head = (v: Vitals | null) => `<div class="perf-head">${perfTitle(v)}<span class="spacer"></span>${devSeg}</div>`;
  screen.innerHTML = `${head(null)}
    ${perfBody(null)}`;
  const v = await fetchVitals();
  if (gen !== generation) return;
  screen.innerHTML = `${head(v)}
    ${perfBody(v)}`;
  settled = gen;
  csv = { vitals: vitalsCsv(v) };
}

/** Desktop Performance under the title; `null` draws it loading. */
function perfBody(v: Vitals | null): string {
  if (!v) return `<div class="slot" data-card="perf"><div class="vitals">${VITALS.map(() => `<section class="vital">${skeleton(150)}</section>`).join("")}</div>${card("Pages", skeleton(220))}</div>`;
  const loads = vitalLoads(v);
  const pct = (f?: number) => `${Math.round((f ?? 0) * 100)}%`;
  const cards = VITALS.map((x) => {
    const m = v.overall[x.key];
    const r = m ? rateVital(x.key, m.p75) : null;
    return `<section class="vital">
      <div class="t"><div style="display:flex;flex-direction:column;gap:2px"><span class="abbr">${x.abbr}</span><span class="full">${x.name}</span></div>
        <span class="pill ${r ?? "none"}">${r ? RATING[r] : "No data"}</span></div>
      <span class="v num">${m ? fmtVital(x.key, m.p75) : "–"}</span>
      ${m && m.good !== undefined
        ? `<div style="display:flex;flex-direction:column;gap:8px">
            <div class="dist" role="img" aria-label="${pct(m.good)} good, ${pct(m.ni)} needs improvement, ${pct(m.poor)} poor">
              <div style="width:${pct(m.good)};background:var(--good)"></div><div style="width:${pct(m.ni)};background:var(--warn)"></div><div style="width:${pct(m.poor)};background:var(--bad)"></div>
            </div>
            <div class="dist-l"><span>${pct(m.good)} good</span><span>${pct(m.ni)}</span><span>${pct(m.poor)} poor</span></div>
          </div>`
        : ""}
      ${lowSample(m?.samples)}
      <span class="thr">${x.thr}</span>
    </section>`;
  }).join("");

  const grid = "grid-template-columns:minmax(0,1fr) 64px 72px 80px 64px 64px";
  const rows = v.rows.length
    ? `<div class="table-scroll"><div class="rows perf"><div class="rhead" style="${grid}"><span>Page</span><span>Loads</span><span>LCP</span><span>INP</span><span>CLS</span><span>TTFB</span></div>${v.rows
        .map((p) => {
          const loadsP = Math.max(0, ...VITALS.map((x) => p[x.key]?.samples ?? 0));
          return `<div class="prow" style="${grid}"><span class="path" title="${esc(p.key)}">${esc(p.key)}</span><span class="n">${fmtN(loadsP)}</span>${VITALS.map((x) => {
            const m = p[x.key];
            if (!m) return `<span class="n">–</span>`;
            const r = rateVital(x.key, m.p75);
            return `<span class="vc"><span class="dot8 ${r}" title="${RATING[r]}"></span><span class="sr">${RATING[r]}: </span>${fmtVital(x.key, m.p75)}</span>`;
          }).join("")}</div>`;
        })
        .join("")}</div></div>`
    : `<div class="empty">No page loads measured yet in this range. Web Vitals are sent when a visitor leaves or hides a page.</div>`;
  const legend = `<div class="dots"><span><i class="dot8 good"></i>Good</span><span><i class="dot8 ni"></i>Needs improvement</span><span><i class="dot8 poor"></i>Poor</span></div>`;

  return `<div class="slot" data-card="perf">${lowSampleNotice(loads)}
    <div class="vitals">${cards}</div>
    ${card("Pages", rows, { csvKey: "vitals", extra: legend })}</div>`;
}

/** Device toggle (desktop and mobile): new vitals for the part of the screen under the toggle. */
function switchDevice(): void {
  const mobile = mq.matches;
  updateCard("perf", async (current) => {
    const v = await fetchVitals();
    if (!current()) return;
    swapCard("perf", mobile ? mPerfBody(v) : perfBody(v));
    const head = document.querySelector(mobile ? ".m-perf-head" : ".perf-head > :first-child");
    if (head) head.outerHTML = mobile ? mPerfHead(v) : perfTitle(v);
    if (!mobile) csv.vitals = vitalsCsv(v);
  });
}



// ---------- Conversions: goals and funnels (not in the handoffs; built from the same card system) ----------

type Kind = "event" | "pageview" | "click";
interface Rule {
  kind: Kind;
  match: string;
}
interface FunnelDefUI {
  id: string;
  name: string;
  window_days: number;
  steps: (Rule & { label: string })[];
}

/** Writes go through the same-origin JSON API (POST to create, PATCH/DELETE by id). */
async function send<T>(method: "POST" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

const CLICK_TARGETS: [string, string][] = [["WhatsApp link", "whatsapp"], ["Phone link", "phone"], ["Email link", "email"], ["Any outbound link", "outbound"]];
const KIND_LABEL: Record<Kind, string> = { event: "Event", pageview: "Page visit", click: "Link click" };

function ruleSummary(r: Rule): string {
  try {
    const m = (typeof r.match === "string" ? JSON.parse(r.match) : r.match) as { name?: string; names?: string[]; path?: string; target?: string };
    if (r.kind === "event") return `Event ${(m.names ?? [m.name]).map((n) => `“${n}”`).join(" or ")}`;
    if (r.kind === "pageview") return `Visits ${m.path}`;
    return CLICK_TARGETS.find(([, v]) => v === m.target)?.[0].replace(" link", " link click") ?? "Link click";
  } catch {
    return "Invalid rule";
  }
}
function ruleValue(r: Rule): string {
  try {
    const m = (typeof r.match === "string" ? JSON.parse(r.match) : r.match) as Record<string, string | string[]>;
    const v = m.names ?? m.name ?? m.path ?? m.target ?? "";
    return Array.isArray(v) ? v.join(", ") : v;
  } catch {
    return "";
  }
}
function ruleFrom(kind: Kind, value: string): Rule {
  const v = value.trim();
  // Several event names, comma separated, match any of them.
  const names = kind === "event" ? v.split(",").map((n) => n.trim()).filter(Boolean) : [];
  const event = names.length > 1 ? { names } : { name: v };
  return { kind, match: JSON.stringify(kind === "event" ? event : kind === "pageview" ? { path: v } : { target: v }) };
}

/** One rule editor: type select plus the right value input for it. */
function ruleFields(prefix: string, r?: Rule): string {
  const kind = r?.kind ?? "event";
  const val = r ? ruleValue(r) : "";
  return `<select class="in" name="${prefix}kind" data-kind-select aria-label="Type">${(Object.keys(KIND_LABEL) as Kind[])
    .map((k) => `<option value="${k}"${k === kind ? " selected" : ""}>${KIND_LABEL[k]}</option>`)
    .join("")}</select>
    <span class="rule-value" data-kind="${kind}">
      <input class="in" name="${prefix}value_event" placeholder="Event name, e.g. form_submit (or several, comma separated)" value="${kind === "event" ? esc(val) : ""}">
      <input class="in" name="${prefix}value_pageview" placeholder="Path, e.g. /thank-you*" value="${kind === "pageview" ? esc(val) : ""}">
      <select class="in" name="${prefix}value_click">${CLICK_TARGETS.map(([l, v]) => `<option value="${v}"${kind === "click" && val === v ? " selected" : ""}>${l}</option>`).join("")}</select>
    </span>`;
}
function readRule(form: HTMLFormElement, prefix: string): Rule {
  const f = new FormData(form);
  const kind = f.get(`${prefix}kind`) as Kind;
  return ruleFrom(kind, String(f.get(`${prefix}value_${kind}`) ?? ""));
}

let editing: { what: "goal" | "funnel"; id?: string } | null = null;
let confirmDelete: string | null = null;
let funnelId: string | null = null;
let funnelStepCount = 3;

const PROBLEMS: Record<string, string> = {
  bad_name: "Give it a name.",
  bad_rule: "That rule isn't complete: check the event name, path or link type.",
  bad_step_rule: "One of the steps isn't complete: check its event name, path or link type.",
  bad_step_label: "Every step needs a label.",
  funnel_needs_2_to_6_steps: "A funnel needs 2 to 6 steps.",
  bad_window: "The window must be 1 to 90 days.",
  funnels_only_last_90_days: "Funnels need each visitor's steps in order, which are kept for the last 90 days. Pick a range within that.",
  bad_value_prop: "The value property is an event property name, like value or order_total: letters, digits, _ - and . only.",
  bad_currency: "Pick a three-letter currency code, like AED or USD.",
};

async function renderConversions(gen: number): Promise<void> {
  screen.innerHTML = `${card("Goals", skeleton(180))}${card("Funnels", skeleton(240))}`;
  const [o, goals, funnels, rev, events, products] = await Promise.all([
    api<Overview>("overview", params()),
    api<GoalDef[]>("goals", { project: state.project }),
    api<FunnelDefUI[]>("funnels", { project: state.project }),
    // Revenue is extra: if it fails, goals and funnels still show (with the error in its place).
    api<Revenue>("revenue", params({ dim: state.rdim })).catch((err: unknown) => err as Error),
    // Events too: a failure shows in its card only.
    api<EventsReport>("events", params()).catch((err: unknown) => err as Error),
    // And products (shops sending view_item / add_to_cart): the card is left out when there are none.
    api<ProductsReport>("products", params()).catch((err: unknown) => err as Error),
  ]);
  if (gen !== generation) return;
  eventsShown = events;
  eventPropsCache = new Map();
  goalEvents = goalEventNames(goals);
  if (openEvent && (events instanceof Error || !events.rows.some((r) => r.name === openEvent))) openEvent = null;
  productsShown = products;
  productDetailCache = new Map();
  if (openProduct && (products instanceof Error || !products.rows.some((r) => r.id === openProduct))) openProduct = null;
  if (!funnels.some((f) => f.id === funnelId)) funnelId = funnels[0]?.id ?? null;
  const stats = new Map(o.goals.map((g) => [g.id, g]));
  const currency = rev instanceof Error ? null : rev.currency;

  // ---- goals ----
  // A Value column once any goal carries one (its summed amounts, in the project's currency).
  const valued = !!currency && goals.some((g) => g.value_prop);
  const grid = `grid-template-columns:minmax(0,1fr) 96px 84px 96px${valued ? " 112px" : ""} 120px`;
  const goalRows = goals.length
    ? `<div class="table-scroll"><div class="rows wide${valued ? " xwide" : ""}"><div class="rhead" style="${grid}"><span>Goal</span><span>Completions</span><span>Visitors</span><span>Conv. rate</span>${valued ? "<span>Value</span>" : ""}<span></span></div>${goals
        .map((g) => {
          const st = stats.get(g.id);
          const actions =
            confirmDelete === g.id
              ? `<button class="link danger" data-act="goal-delete-yes" data-id="${g.id}">Delete</button> <button class="link" data-act="cancel">Keep</button>`
              : `<button class="link" data-act="goal-edit" data-id="${g.id}">Edit</button> <button class="link" data-act="goal-delete" data-id="${g.id}">Delete</button>`;
          const summary = `${ruleSummary(g)}${g.value_prop ? ` · value from “${g.value_prop}”` : ""}`;
          return `<div class="row" style="${grid}"><span class="gname"><b>${esc(g.name)}</b><span>${esc(summary)}</span></span>
            <span class="n p">${fmtN(st?.completions ?? 0)}</span><span class="n">${fmtN(st?.visitors ?? 0)}</span><span class="n">${fmtPct1(st?.rate)}</span>${valued ? `<span class="n">${g.value_prop ? fmtMoney(st?.value ?? 0, currency!) : "–"}</span>` : ""}<span class="n actions">${actions}</span></div>`;
        })
        .join("")}</div></div>`
    : `<div class="empty">No goals yet. Add one for each action that counts as a conversion: a form sent, a WhatsApp tap, a thank-you page.</div>`;
  const goalForm =
    editing?.what === "goal"
      ? (() => {
          const g = goals.find((x) => x.id === editing!.id);
          // "Make this a goal" (Events card) fills in a new goal with the event.
          const pre = !g && goalPrefill ? goalPrefill : null;
          const rule: Rule | undefined = g ?? (pre ? { kind: "event", match: JSON.stringify({ name: pre.event }) } : undefined);
          return `<form class="editor" data-form="goal" data-id="${g?.id ?? ""}">
            <h3>${g ? "Edit goal" : "New goal"}</h3>
            <label class="fld"><span>Name</span><input class="in" name="name" required maxlength="100" value="${esc(g?.name ?? pre?.name ?? "")}" placeholder="e.g. Contact form sent"></label>
            <div class="fld"><span>Counts when</span><div class="rule">${ruleFields("", rule)}</div></div>
            <label class="fld"><span>Value property <span class="muted">(optional)</span></span><input class="in" name="value_prop" maxlength="64" value="${esc(g?.value_prop ?? "")}" placeholder="e.g. value" autocomplete="off" spellcheck="false">
              <span class="fld-help">The event property holding the amount, e.g. <span class="mono">value</span> for <span class="mono">mb('track', 'purchase', { value: 250 })</span>. Goals with a value add up to revenue, in ${esc(currency ?? "the project's currency")}.</span></label>
            <div class="form-err" hidden></div>
            <div class="btns"><button class="btn primary" type="submit">${g ? "Save" : "Add goal"}</button><button class="btn" type="button" data-act="cancel">Cancel</button></div>
          </form>`;
        })()
      : "";

  const revenue = rev instanceof Error ? errorCard(rev) : rev.enabled ? revenueSection(rev) : "";

  // Mobile has no toolbar, and revenue depends on the range, so Conversions gets the Traffic head.
  screen.innerHTML = `${mq.matches ? mHead() : ""}${revenue}${productsCard(products)}${eventsCard(events)}<section class="card"><div class="card-head"><h2>Goals</h2><span class="spacer"></span>${editing?.what === "goal" ? "" : `<button class="link" data-act="goal-new">Add goal</button>`}</div>${goalForm}${goalRows}</section>
  ${funnelCard(funnels, null)}`;
  syncRuleInputs();
  csv = {};
  if (!(rev instanceof Error) && rev.enabled) {
    // Keep the chosen dimension in view when the switcher is wider than the card.
    const pressed = document.querySelector<HTMLElement>('.seg-scroll [aria-pressed="true"]');
    const strip = pressed?.closest<HTMLElement>(".seg-scroll");
    if (pressed && strip) strip.scrollLeft = Math.max(0, pressed.offsetLeft - (strip.clientWidth - pressed.offsetWidth) / 2);
    areaChart($("rev-chart"), {
      labels: rev.daily.map((d) => dayLabel(d.day)),
      current: rev.daily.map((d) => d.revenue),
      previous: rev.previous_daily.length ? rev.previous_daily.map((d) => d.revenue) : undefined,
      metric: "Revenue",
      format: (v) => fmtMoney(v, rev.currency),
      tick: (v) => fmtMoney(v, rev.currency).replace(`${rev.currency} `, ""),
      compact: mq.matches,
    });
    csv = {
      "revenue-daily": {
        name: "revenue-daily",
        rows: () => rev.daily.map((d, i) => ({ day: d.day, currency: rev.currency, revenue: d.revenue, conversions: d.conversions, previous_day: rev.previous_daily[i]?.day ?? "", previous_revenue: rev.previous_daily[i]?.revenue ?? "" })),
      },
      revenue: revenueCsv(rev),
    };
  }
  if (!(events instanceof Error) && events.rows.length) csv.events = eventsCsv(events);
  if (!(products instanceof Error) && products.rows.length) csv.products = productsCsv(products);
  funnelsShown = funnels;
  settled = gen;
  if (openEvent) void fillEventProps(claim("events", gen));
  if (openProduct) void fillProductDetail(claim("products", gen));
  if (funnelId && editing?.what !== "funnel") await fillFunnel(claim("funnel", gen));
}

/** The funnel definitions on screen, so picking another funnel redraws only its card. */
let funnelsShown: FunnelDefUI[] = [];

/** The Funnels card: picker, actions and the picked funnel (`null` while it loads), or the editor. */
function funnelCard(funnels: FunnelDefUI[], report: FunnelReport | Error | null): string {
  let funnelBody = "";
  if (editing?.what === "funnel") {
    const f = funnels.find((x) => x.id === editing!.id);
    const steps = f?.steps ?? [];
    const n = Math.max(2, Math.min(6, f ? steps.length : funnelStepCount));
    funnelBody = `<form class="editor" data-form="funnel" data-id="${f?.id ?? ""}">
      <h3>${f ? "Edit funnel" : "New funnel"}</h3>
      <div class="two-fld"><label class="fld"><span>Name</span><input class="in" name="name" required maxlength="100" value="${esc(f?.name ?? "")}" placeholder="e.g. Enquiry"></label>
      <label class="fld narrow"><span>Within (days)</span><input class="in" name="window" type="number" min="1" max="90" value="${f?.window_days ?? 7}"></label></div>
      <ol class="steps">${Array.from({ length: n }, (_, i) => {
        const st = steps[i];
        return `<li><span class="num-badge">${i + 1}</span><input class="in" name="s${i}label" placeholder="Step label" maxlength="100" value="${esc(st?.label ?? "")}"><div class="rule">${ruleFields(`s${i}`, st)}</div></li>`;
      }).join("")}</ol>
      <div class="btns"><button class="btn" type="button" data-act="step-add"${n >= 6 ? " disabled" : ""}>Add step</button><button class="btn" type="button" data-act="step-remove"${n <= 2 ? " disabled" : ""}>Remove last step</button></div>
      <div class="form-err" hidden></div>
      <div class="btns"><button class="btn primary" type="submit">${f ? "Save" : "Create funnel"}</button><button class="btn" type="button" data-act="cancel">Cancel</button></div>
    </form>`;
  } else if (!funnels.length) {
    funnelBody = `<div class="empty">No funnels yet. A funnel follows visitors through 2 to 6 steps in order, like home → work page → contact form, and shows where they drop off.</div>`;
  } else {
    const body =
      report === null
        ? skeleton(200)
        : report instanceof Error
          ? `<div class="notice">${esc(PROBLEMS[report.message] ?? report.message)}</div>`
          : funnelChart(report);
    funnelBody = `<div id="funnel-body">${body}</div>`;
  }
  const funnelPicker = funnels.length && editing?.what !== "funnel" ? seg("funnel", funnels.map((f) => [esc(f.name), f.id] as [string, string]), funnelId ?? "") : "";
  const funnelActions =
    editing?.what === "funnel"
      ? ""
      : `${funnelId ? (confirmDelete === funnelId ? `<button class="link danger" data-act="funnel-delete-yes" data-id="${funnelId}">Delete funnel</button><button class="link" data-act="cancel">Keep</button>` : `<button class="link" data-act="funnel-edit" data-id="${funnelId}">Edit</button><button class="link" data-act="funnel-delete" data-id="${funnelId}">Delete</button>`) : ""}<button class="link" data-act="funnel-new">New funnel</button>`;
  return `<section class="card" data-card="funnel"><div class="card-head"><h2>Funnels</h2>${funnelPicker}<span class="spacer"></span><span class="card-actions">${funnelActions}</span></div>${funnelBody}</section>`;
}

/** Loads the picked funnel into its card (unless a later pick or a refresh took over). */
async function fillFunnel(current: () => boolean): Promise<void> {
  let r: FunnelReport | Error;
  try {
    r = await api<FunnelReport>("funnel", params({ id: funnelId! }));
  } catch (err) {
    r = err as Error;
  }
  if (current()) swapCard("funnel", funnelCard(funnelsShown, r));
}

/** Funnel picker: the card's head changes at once (its Edit/Delete act on the new pick), then its chart loads. */
function switchFunnel(id: string): void {
  funnelId = id;
  if (funnelsShown.some((f) => f.id === confirmDelete)) confirmDelete = null;
  updateCard("funnel", async (current) => {
    swapCard("funnel", funnelCard(funnelsShown, null));
    await fillFunnel(current);
  });
}

// ---------- Events (Conversions): every custom event the site or app sends, and its properties ----------

/** The events report on screen, so opening a row redraws only the Events card. */
let eventsShown: EventsReport | Error | null = null;
/** The event whose properties are open (kept across refreshes while it's still listed). */
let openEvent: string | null = null;
let eventQuery = "";
/** Property breakdowns loaded for the range on screen; replaced on every full render. */
let eventPropsCache = new Map<string, EventProps | Error>();
/** Event names some goal already counts. */
let goalEvents = new Set<string>();
/** "Make this a goal": the goal editor opens with these filled in. */
let goalPrefill: { name: string; event: string } | null = null;
/** A search box once the list is longer than this. */
const EVENT_SEARCH_OVER = 12;

function goalEventNames(goals: GoalDef[]): Set<string> {
  const out = new Set<string>();
  for (const g of goals) {
    if (g.kind !== "event") continue;
    try {
      const m = JSON.parse(g.match) as { name?: unknown; names?: unknown };
      for (const n of Array.isArray(m.names) ? m.names : [m.name]) if (typeof n === "string") out.add(n);
    } catch {}
  }
  return out;
}

/** "add_to_cart" → "Add to cart", as a starting name for a goal. */
const eventGoalName = (n: string) => cap(n.replace(/[_\-.]+/g, " ").replace(/\s+/g, " ").trim()) || n;
const fmtVal = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2 });
const eventMatches = (name: string) => !eventQuery || name.toLowerCase().includes(eventQuery.trim().toLowerCase());

/** The Events card; empty (no card) when nothing was sent in the range and no filter is on. */
function eventsCard(rep: EventsReport | Error): string {
  const slot = { slot: "events" };
  if (rep instanceof Error) return card("Events", errorCard(rep), slot);
  if (!rep.available)
    return card("Events", `<div class="notice">${esc(rep.reason === "hot_store_not_configured" ? ERRORS.hot_store_not_configured! : "Events are kept for the last 90 days. Pick a range within that to see them.")}</div>`, slot);
  if (!rep.rows.length && !Object.keys(state.filters).length) return "";
  const search = rep.rows.length > EVENT_SEARCH_OVER
    ? `<input class="in ev-search" type="search" data-ev-search placeholder="Search events" aria-label="Search events" value="${esc(eventQuery)}" autocomplete="off" spellcheck="false">`
    : "";
  const body = rep.rows.length ? eventsTable(rep) : `<div class="empty">No custom events match these filters in this range.</div>`;
  const notes = [
    rep.rows.length >= rep.limit ? `The ${rep.limit} most frequent events are listed.` : "",
    rep.rows.length ? "Open an event to see what it was sent with." : "",
  ].filter(Boolean).join(" ");
  return card("Events", `${body}${notes ? `<p class="rev-note">${notes}</p>` : ""}`, { ...slot, csvKey: rep.rows.length ? "events" : undefined, extra: search, cls: "events" });
}

function eventsTable(rep: EventsReport): string {
  const max = Math.max(1, ...rep.rows.map((r) => r.count));
  const mobile = mq.matches;
  const grid = "grid-template-columns:minmax(0,1fr) 84px 84px 72px 56px";
  const goalPill = (name: string) => (goalEvents.has(name) ? `<span class="pill none ev-goal" title="A goal counts this event">Goal</span>` : "");
  const rows = rep.rows
    .map((r) => {
      const open = openEvent === r.name;
      const hidden = eventMatches(r.name) ? "" : " hidden";
      const w = ((r.count / max) * 100).toFixed(1);
      const attrs = `data-event="${esc(r.name)}" role="button" tabindex="0" aria-expanded="${open}"`;
      const row = mobile
        ? `<div class="m-src clickable ev-row" ${attrs}><div class="a"><span><span class="ev-caret" aria-hidden="true">▸</span>${esc(r.name)}</span>${goalPill(r.name)}<b>${fmtN(r.count)}</b></div>
            <div class="t"><div style="width:${w}%"></div></div><span class="m">${plural(r.visitors, "visitor")} ${deltaPill(r.count, r.previous_count)}</span></div>`
        : `<div class="row clickable ev-row" style="${grid}" ${attrs}><div class="barcell"><div class="bar" style="width:${w}%"></div><span class="lbl"><span class="ev-caret" aria-hidden="true">▸</span><span class="ev-name">${esc(r.name)}</span></span></div>
            <span class="n p">${fmtN(r.count)}</span><span class="n">${fmtN(r.visitors)}</span><span class="n">${deltaPill(r.count, r.previous_count)}</span><span class="n">${goalPill(r.name)}</span></div>`;
      return `<div class="ev${open ? " open" : ""}" data-ev-name="${esc(r.name)}"${hidden}>${row}${open ? eventDetail(r.name, eventPropsCache.get(r.name)) : ""}</div>`;
    })
    .join("");
  const none = `<div class="empty" data-ev-none${rep.rows.some((r) => eventMatches(r.name)) ? " hidden" : ""}>No events match “<span data-ev-q>${esc(eventQuery.trim())}</span>”.</div>`;
  if (mobile) return `<div class="ev-list">${rows}</div>${none}`;
  const head = `<div class="rhead" style="${grid}"><span>Event</span><span>Count</span><span>Visitors</span><span>Change</span><span></span></div>`;
  return `<div class="table-scroll"><div class="rows wide">${head}${rows}</div></div>${none}`;
}

/** An open event: its properties (bars per value, or a numeric summary) and the goal action. */
function eventDetail(name: string, p: EventProps | Error | undefined): string {
  const action = goalEvents.has(name)
    ? `<span class="pill none">Goal</span><span class="muted">A goal already counts this event.</span>`
    : canEditProject()
      ? `<button class="btn" data-ev-goal="${esc(name)}">Make this a goal</button>`
      : "";
  let body: string;
  if (p === undefined) body = skeleton(120);
  else if (p instanceof Error) body = errorCard(p);
  else if (!p.available) body = `<div class="notice">Event properties are kept for the last 90 days.</div>`;
  else if (!p.keys.length) body = `<div class="empty">${p.events ? "This event is sent without properties." : "No properties recorded in this range."}</div>`;
  else {
    const total = Math.max(1, p.events);
    const blocks = p.keys
      .map((k) => {
        const share = `${fmtPct(k.count / total)} of events`;
        const inner = k.kind === "numeric" && k.numeric
          ? `<div class="ev-num">${[["Count", fmtN(k.numeric.count)], ["Average", fmtVal(k.numeric.avg)], ["Min", fmtVal(k.numeric.min)], ["Max", fmtVal(k.numeric.max)]]
              .map(([l, v]) => `<div><span>${l}</span><b class="num">${v}</b></div>`)
              .join("")}</div>`
          : `${mBars(k.values ?? [], { name: (v) => `<span class="l">${esc(v.value)}</span>`, value: (v) => fmtN(v.count), bar: (v) => v.count, warm: true })}${
              k.other ? `<div class="ev-more">${plural(k.other, "more event")} with ${k.distinct_capped ? "many" : fmtN(Math.max(0, (k.distinct ?? 0) - (k.values?.length ?? 0)))} other values</div>` : ""
            }`;
        return `<div class="ev-prop"><div class="ev-prop-h"><span class="mono">${esc(k.key)}</span><span>${share}</span></div>${inner}</div>`;
      })
      .join("");
    const notes = [
      p.sampled ? `Based on the newest ${fmtN(p.rows)} of these events.` : "",
      p.more_keys ? `${plural(p.more_keys, "other property")} not shown.` : "",
      p.empty ? `${plural(p.empty, "event")} had no properties.` : "",
      p.skipped ? `${plural(p.skipped, "event")} had properties too long to read (over 2 KB).` : "",
    ].filter(Boolean).join(" ");
    body = `<div class="ev-props">${blocks}</div>${notes ? `<p class="rev-note">${notes}</p>` : ""}`;
  }
  return `<div class="ev-detail">${body}${action ? `<div class="ev-actions">${action}</div>` : ""}</div>`;
}

const eventsCsv = (rep: EventsReport) => ({
  name: "events",
  rows: () =>
    rep.rows.map((r) => ({
      event: r.name,
      count: r.count,
      visitors: r.visitors,
      previous_count: r.previous_count ?? "",
      change_pct: r.previous_count ? Math.round(((r.count - r.previous_count) / r.previous_count) * 1000) / 10 : "",
      first_seen: r.first_seen ? new Date(r.first_seen).toISOString() : "",
      last_seen: r.last_seen ? new Date(r.last_seen).toISOString() : "",
      goal: goalEvents.has(r.name) ? "yes" : "no",
    })),
});

/** Loads the open event's properties into the card, once per render (unless a later click took over). */
async function fillEventProps(current: () => boolean): Promise<void> {
  const name = openEvent;
  const rep = eventsShown;
  if (!name || !rep || rep instanceof Error || eventPropsCache.has(name)) return;
  const cache = eventPropsCache;
  let p: EventProps | Error;
  try {
    p = await api<EventProps>("event_props", params({ name }));
  } catch (err) {
    p = err as Error;
  }
  cache.set(name, p);
  if (current() && openEvent === name) swapCard("events", eventsCard(rep));
}

/** Clicking an event opens (or closes) its properties in place; the rest of the screen stays put. */
function toggleEvent(name: string): void {
  openEvent = openEvent === name ? null : name;
  const rep = eventsShown;
  if (!rep || rep instanceof Error) return;
  updateCard("events", async (current) => {
    swapCard("events", eventsCard(rep));
    await fillEventProps(current);
  });
}

/** Search box: hides rows in place, so typing never redraws the card. */
function searchEvents(q: string): void {
  eventQuery = q;
  let any = false;
  for (const el of document.querySelectorAll<HTMLElement>("[data-ev-name]")) {
    const show = eventMatches(el.dataset.evName ?? "");
    el.hidden = !show;
    any ||= show;
  }
  const none = document.querySelector<HTMLElement>("[data-ev-none]");
  if (none) {
    none.hidden = any;
    const q2 = none.querySelector("[data-ev-q]");
    if (q2) q2.textContent = q.trim();
  }
}

/** "Make this a goal": the goal editor, filled in with the event. */
function makeEventGoal(name: string): void {
  editing = { what: "goal" };
  confirmDelete = null;
  goalPrefill = { name: eventGoalName(name), event: name };
  void refresh().then(() => {
    const form = document.querySelector<HTMLFormElement>('form[data-form="goal"]');
    form?.scrollIntoView({ block: "center" });
    form?.querySelector<HTMLInputElement>('[name="name"]')?.focus({ preventScroll: true });
  });
}

// ---------- Products (Conversions): what shoppers looked at, added to the cart and bought ----------

type ProductSort = "name" | "views" | "carts" | "cart_rate" | "cart_value" | "purchases" | "change";
/** The products report on screen: search, brand, sort and opening a row redraw only its list. */
let productsShown: ProductsReport | Error | null = null;
let openProduct: string | null = null;
let productQuery = "";
let productBrand = "";
let productSort: { key: ProductSort; asc: boolean } = { key: "carts", asc: false };
/** The list shows this many rows until "Show all". */
const PRODUCTS_SHOWN = 25;
let productsAll = false;
/** One product's breakdowns loaded for the range on screen; replaced on every full render. */
let productDetailCache = new Map<string, ProductDetail | Error>();

const PRODUCT_SORTS: [string, ProductSort][] = [
  ["Added to cart", "carts"], ["Views", "views"], ["Cart rate", "cart_rate"], ["Cart value", "cart_value"], ["Purchased", "purchases"], ["Change", "change"], ["Name", "name"],
];
const productName = (r: ProductRow) => r.name || r.id;
const productChange = (r: ProductRow) => (r.previous_carts ? (r.carts - r.previous_carts) / r.previous_carts : r.carts > 0 && r.previous_carts === 0 ? Infinity : null);

function productSortValue(r: ProductRow, key: ProductSort): number | string | null {
  switch (key) {
    case "name": return productName(r).toLowerCase();
    case "change": return productChange(r);
    default: return r[key];
  }
}

/** The rows the search, brand and sort leave, in order. */
function productsVisible(rep: ProductsReport): ProductRow[] {
  const q = productQuery.trim().toLowerCase();
  const rows = rep.rows.filter(
    (r) => (!productBrand || r.brand === productBrand) && (!q || `${r.name}\n${r.id}\n${r.brand}`.toLowerCase().includes(q)),
  );
  const { key, asc } = productSort;
  // Missing values (no cart rate, nothing bought) go last whichever way the column is sorted.
  return rows.sort((a, b) => {
    const x = productSortValue(a, key);
    const y = productSortValue(b, key);
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    const c = typeof x === "string" ? x.localeCompare(String(y)) : x === y ? 0 : x - (y as number);
    return (asc ? c : -c) || b.carts - a.carts || b.views - a.views || a.id.localeCompare(b.id);
  });
}

/** The Products card: hidden for sites that send no product events (and for older ranges unless the shop sent some lately). */
function productsCard(rep: ProductsReport | Error): string {
  const slot = { slot: "products", cls: "products" };
  if (rep instanceof Error) return card("Products", errorCard(rep), slot);
  if (!rep.available)
    return rep.seen ? card("Products", `<div class="notice">Product views and adds to cart are kept for the last 90 days. Pick a range within that to see them.</div>`, slot) : "";
  if (!rep.rows.length) return "";
  const brands = [...rep.rows.reduce((m, r) => (r.brand ? m.set(r.brand, (m.get(r.brand) ?? 0) + 1) : m), new Map<string, number>())].sort((a, b) => a[0].localeCompare(b[0]));
  if (productBrand && !brands.some(([b]) => b === productBrand)) productBrand = "";
  const controls = `<div class="pr-controls">
    <input class="in pr-search" type="search" data-prod-search placeholder="Search products or SKUs" aria-label="Search products" value="${esc(productQuery)}" autocomplete="off" spellcheck="false">
    ${brands.length > 1 ? `<select class="in pr-brand" data-prod-brand aria-label="Brand"><option value="">All brands</option>${brands.map(([b, n]) => `<option value="${esc(b)}"${b === productBrand ? " selected" : ""}>${esc(b)} (${n})</option>`).join("")}</select>` : ""}
    ${mq.matches ? `<select class="in pr-order" data-prod-sort-select aria-label="Sort by">${PRODUCT_SORTS.map(([l, k]) => `<option value="${k}"${k === productSort.key ? " selected" : ""}>${l}</option>`).join("")}</select>` : ""}
  </div>`;
  const notes = [
    rep.rows.length >= rep.limit ? `The ${rep.limit} products added to the cart most (then viewed most) are listed.` : "",
    rep.purchases_sampled ? "Purchases are counted from the newest 2,000 orders." : "",
    "Added to cart counts Add to cart and Buy it now. Open a product to see where its shoppers came from.",
  ].filter(Boolean).join(" ");
  return card("Products", `${controls}<div data-prod-list>${productList(rep)}</div><p class="rev-note">${notes}</p>`, { ...slot, csvKey: "products" });
}

/** The product cell: name, then brand and SKU. */
const productCell = (r: ProductRow) =>
  `<span class="pr-name"><span class="pr-t" title="${esc(productName(r))}"><span class="ev-caret" aria-hidden="true">▸</span>${esc(productName(r))}</span><span class="pr-s">${r.brand ? `${esc(r.brand)} · ` : ""}<span class="mono">${esc(r.id)}</span></span></span>`;

function productList(rep: ProductsReport): string {
  const cur = rep.currency;
  const rows = productsVisible(rep);
  if (!rows.length) return `<div class="empty">No products match${productQuery.trim() ? ` “${esc(productQuery.trim())}”` : ""}${productBrand ? ` in ${esc(productBrand)}` : ""}.</div>`;
  const shown = productsAll || rows.length <= PRODUCTS_SHOWN + 5 ? rows : rows.slice(0, PRODUCTS_SHOWN);
  const mobile = mq.matches;
  const grid = "grid-template-columns:minmax(0,1fr) 72px 104px 80px 116px 84px 64px";
  const bought = (r: ProductRow) => (r.purchases ? fmtN(r.purchases) : "–");
  const body = shown
    .map((r) => {
      const open = openProduct === r.id;
      const attrs = `data-product="${esc(r.id)}" role="button" tabindex="0" aria-expanded="${open}"`;
      const row = mobile
        ? `<div class="m-src clickable pr-row" ${attrs}><div class="a">${productCell(r)}<b>${fmtN(r.carts)}<small>added</small></b></div>
            <span class="m">${[plural(r.views, "view"), r.cart_rate !== null ? `${fmtPct(r.cart_rate)} cart rate` : "", r.cart_value ? fmtMoney(r.cart_value, cur) : "", r.purchases ? `${fmtN(r.purchases)} bought` : ""].filter(Boolean).join(" · ")} ${deltaPill(r.carts, r.previous_carts)}</span></div>`
        : `<div class="row clickable pr-row" style="${grid}" ${attrs}>${productCell(r)}<span class="n">${fmtN(r.views)}</span><span class="n p">${fmtN(r.carts)}</span><span class="n">${fmtPct(r.cart_rate)}</span><span class="n">${r.cart_value ? fmtMoney(r.cart_value, cur) : "–"}</span><span class="n">${bought(r)}</span><span class="n">${deltaPill(r.carts, r.previous_carts)}</span></div>`;
      return `<div class="ev pr${open ? " open" : ""}">${row}${open ? productDetailHtml(r, cur, productDetailCache.get(r.id)) : ""}</div>`;
    })
    .join("");
  const more = shown.length < rows.length ? `<button class="link pr-more" data-prod-more>Show all ${fmtN(rows.length)} products</button>` : "";
  if (mobile) return `<div class="ev-list pr-list">${body}</div>${more}`;
  const th = (labelText: string, key: ProductSort) => {
    const on = productSort.key === key;
    return `<button class="pr-sort${on ? " on" : ""}" data-prod-sort="${key}" aria-sort="${on ? (productSort.asc ? "ascending" : "descending") : "none"}">${labelText}${on ? (productSort.asc ? " ↑" : " ↓") : ""}</button>`;
  };
  const head = `<div class="rhead" style="${grid}">${th("Product", "name")}${th("Views", "views")}${th("Added to cart", "carts")}${th("Cart rate", "cart_rate")}${th("Cart value", "cart_value")}${th("Purchased", "purchases")}${th("Change", "change")}</div>`;
  return `<div class="table-scroll"><div class="rows pr-table">${head}${body}</div></div>${more}`;
}

/** An open product: its numbers, where its adds to cart came from, and its page. */
function productDetailHtml(r: ProductRow, cur: string, d: ProductDetail | Error | undefined): string {
  const facts = [
    ["Viewed by", plural(r.view_visitors, "visitor")],
    ["Added by", plural(r.cart_visitors, "visitor")],
    ["Units added", fmtN(r.quantity)],
    ["Cart value", fmtMoney(r.cart_value, cur)],
    ["Units bought", r.purchases === null ? "–" : fmtN(r.purchases)],
    ["Revenue", r.revenue === null ? "–" : fmtMoney(r.revenue, cur)],
  ]
    .map(([l, v]) => `<div><span>${l}</span><b class="num">${v}</b></div>`)
    .join("");
  let body: string;
  if (d === undefined) body = skeleton(120);
  else if (d instanceof Error) body = errorCard(d);
  else if (!d.available) body = `<div class="notice">Product details are kept for the last 90 days.</div>`;
  else if (!d.carts) body = `<div class="empty">Nobody added this product to the cart in this range.</div>`;
  else {
    const block = (title: string, rows: ProductDetail["channels"], dim: FilterKey, name: (k: string) => string) =>
      `<div class="ev-prop"><div class="ev-prop-h"><span>${title}</span><span>Adds to cart</span></div>${mBars(rows, {
        name: (x) => `<span class="l">${esc(name(x.key))}</span>`,
        value: (x) => fmtN(x.count),
        bar: (x) => x.count,
        warm: true,
        filter: (x) => (x.key ? [dim, x.key] : null),
      })}</div>`;
    body = `<div class="ev-props">${block("Channels", d.channels, "channel", (k) => label("channel", k))}${block("Sources", d.sources, "source", (k) => label("source", k))}${block("Countries", d.countries, "country", (k) => countryName(k))}${block("Devices", d.devices, "device", (k) => label("device", k))}</div>`;
  }
  const link = r.page && r.page.host ? `<a class="link" href="https://${esc(r.page.host)}${esc(r.page.path)}" target="_blank" rel="noopener">Open product page ↗</a>` : "";
  return `<div class="ev-detail pr-detail"><div class="ev-num pr-facts">${facts}</div>${body}${link ? `<div class="ev-actions">${link}</div>` : ""}</div>`;
}

const productsCsv = (rep: ProductsReport) => ({
  name: "products",
  rows: () =>
    productsVisible(rep).map((r) => ({
      item_id: r.id,
      name: r.name,
      brand: r.brand,
      views: r.views,
      view_visitors: r.view_visitors,
      added_to_cart: r.carts,
      cart_visitors: r.cart_visitors,
      cart_rate: r.cart_rate === null ? "" : Math.round(r.cart_rate * 1000) / 1000,
      units_added: r.quantity,
      currency: rep.currency,
      cart_value: r.cart_value,
      units_bought: r.purchases ?? "",
      revenue: r.revenue ?? "",
      previous_added_to_cart: r.previous_carts ?? "",
      product_page: r.page && r.page.host ? `https://${r.page.host}${r.page.path}` : "",
    })),
});

/** Redraws the product list only (the search box keeps focus and what's typed). */
function redrawProducts(): void {
  const rep = productsShown;
  const el = document.querySelector<HTMLElement>("[data-prod-list]");
  if (!el || !rep || rep instanceof Error || !rep.available) return;
  el.innerHTML = productList(rep);
}

/** Loads the open product's breakdown into its row, once per render (unless a later click took over). */
async function fillProductDetail(current: () => boolean): Promise<void> {
  const id = openProduct;
  const rep = productsShown;
  if (!id || !rep || rep instanceof Error || productDetailCache.has(id)) return;
  const cache = productDetailCache;
  let d: ProductDetail | Error;
  try {
    d = await api<ProductDetail>("product", params({ id }));
  } catch (err) {
    d = err as Error;
  }
  cache.set(id, d);
  if (current() && openProduct === id) redrawProducts();
}

function toggleProduct(id: string): void {
  openProduct = openProduct === id ? null : id;
  redrawProducts();
  if (openProduct && settled === generation) void fillProductDetail(claim("products"));
}

function sortProducts(key: ProductSort): void {
  // A second click on the same column flips it; a new column starts high to low (names A to Z).
  productSort = productSort.key === key ? { key, asc: !productSort.asc } : { key, asc: key === "name" };
  redrawProducts();
}

// ---------- Revenue (Conversions): goals that carry a value, credited to the visit's source ----------

const RDIM_TABS: [string, RevenueDimension][] = [
  ["Channels", "channel"], ["Sources", "source"], ["Referrers", "referrer"], ["UTM source", "utm_source"], ["UTM medium", "utm_medium"],
  ["Campaigns", "utm_campaign"], ["Entry pages", "entry_page"], ["Countries", "country"], ["Devices", "device"],
];
const RDIM_HEAD: Record<RevenueDimension, string> = {
  channel: "Channel", source: "Source", referrer: "Referrer", utm_source: "UTM source", utm_medium: "UTM medium",
  utm_campaign: "Campaign", entry_page: "Entry page", country: "Country", device: "Device",
};
/** Which dashboard filter a revenue row applies when clicked (entry pages have none: the page filter matches any view). */
const RDIM_FILTER: Partial<Record<RevenueDimension, FilterKey>> = {
  channel: "channel", source: "source", referrer: "referrer", utm_source: "utm_source", utm_medium: "utm_medium",
  utm_campaign: "utm_campaign", country: "country", device: "device",
};
const canEditProject = () => !!me && (me.role === "admin" || !!me.can_edit?.includes(state.project));

function revenueSection(rev: Revenue): string {
  const cur = rev.currency;
  const t = rev.totals;
  const p = rev.previous;
  const kpi = (labelText: string, value: string, delta: string, foot = "") =>
    `<div class="rev-kpi"><span class="label">${labelText}</span><span class="value num" title="${esc(value)}">${value}</span><span class="foot">${delta}${foot ? `<span>${foot}</span>` : ""}</span></div>`;
  const kpis = [
    kpi("Revenue", fmtMoney(t.revenue, cur), deltaPill(t.revenue, p?.revenue)),
    kpi("Conversions", fmtN(t.conversions), deltaPill(t.conversions, p?.conversions), plural(t.converters, "buyer")),
    kpi("Per visitor", fmtMoney(t.per_visitor, cur), deltaPill(t.per_visitor, p?.per_visitor), plural(t.visitors, "visitor")),
    kpi("Per conversion", fmtMoney(t.per_conversion, cur), deltaPill(t.per_conversion, p && p.conversions ? p.revenue / p.conversions : null)),
  ].join("");

  // The currency is set in one place, Settings → Site settings → Site; this links there.
  const currencyCtl = `<span class="cur-code" title="Amounts are in ${esc(cur)}, as sent by the site. They are never converted.">${esc(cur)}</span>${canEditProject() ? `<button class="link" data-act="currency-edit">Change in Settings</button>` : ""}`;
  const from = rev.goals.map((g) => `${esc(g.name)} <span class="mono">(${esc(g.value_prop)})</span>`).join(", ");
  const first = rev.daily[0]?.day ?? rev.range.from;
  const note = rev.source === "rollup"
    ? `<div class="notice">This range is older than 90 days, so revenue comes from daily summaries. Days summarised before revenue tracking was added show none, and buyers are added up per day.</div>`
    : "";

  return `${note}<section class="card flush rev">
    <div class="rev-head"><h2>Revenue</h2>${currencyCtl}<span class="spacer"></span><span class="rev-from">From ${from}</span></div>
    <div class="rev-kpis">${kpis}</div>
    <div class="chart-wrap">
      <div class="chart-head"><h2>Revenue per day</h2><span class="spacer"></span>
        <div class="legend"><span><i class="cur"></i>${dayLabel(first)} – ${dayLabel(rev.range.to)}</span>${rev.previous_daily.length ? `<span><i class="prev"></i>Previous period</span>` : ""}${exportLink("revenue-daily")}</div>
      </div>
      <div id="rev-chart"></div>
    </div>
  </section>
  ${revenueByCard(rev)}`;
}

const fetchRevenue = () => api<Revenue>("revenue", params({ dim: state.rdim }));
const revenueCsv = (rev: Revenue) => ({
  name: `revenue-by-${rev.dimension.replace("_", "-")}`,
  rows: () => rev.rows.map((r) => ({ [rev.dimension]: r.key, visitors: r.visitors ?? "", conversions: r.conversions, converters: r.converters, currency: rev.currency, revenue: r.revenue, revenue_per_visitor: r.per_visitor ?? "" })),
});

/** "Revenue by source", with its dimension switcher (rdim). */
function revenueByCard(rev: Revenue): string {
  const cur = rev.currency;
  const dim = rev.dimension;
  const fkey = RDIM_FILTER[dim];
  const rowName = (r: Revenue["rows"][number]) => (dim === "country" ? countryName(r.key) : label(dim, r.key));
  const topRev = Math.max(1, ...rev.rows.map((r) => r.revenue));
  // Mobile: revenue up front in each row (the desktop table would put it off-screen).
  const mobileRows = rev.rows.length
    ? rev.rows
        .map((r) => {
          const click = fkey && r.key && !state.filters[fkey] ? ` clickable" data-filter="${fkey}" data-value="${esc(r.key)}` : "";
          const meta = [r.visitors !== null ? plural(r.visitors, "visitor") : "", plural(r.conversions, "conversion"), r.per_visitor !== null ? `${fmtMoney(r.per_visitor, cur)} per visitor` : ""].filter(Boolean).join(" · ");
          return `<div class="m-src${click}"><div class="a"><span${dim === "entry_page" ? ' class="mono"' : ""}>${esc(rowName(r))}</span><b>${fmtMoney(r.revenue, cur)}</b></div>
            <div class="t"><div style="width:${((r.revenue / topRev) * 100).toFixed(1)}%"></div></div><span class="m">${meta}</span></div>`;
        })
        .join("")
    : `<div class="empty">No data for this range yet.</div>`;
  const table = mq.matches ? mobileRows : rankTable(rev.rows, {
    head: RDIM_HEAD[dim],
    name: (r) =>
      dim === "entry_page"
        ? pathLabel(r.key)
        : dim === "country"
          ? `<span class="lbl center"><span class="iso">${esc(r.key || "??")}</span>${esc(countryName(r.key))}</span>`
          : textLabel(label(dim, r.key)),
    bar: (r) => r.revenue,
    cls: "wide",
    cols: [
      { head: "Visitors", width: 84, value: (r) => (r.visitors === null ? "–" : fmtN(r.visitors)) },
      { head: "Conversions", width: 100, value: (r) => fmtN(r.conversions) },
      { head: "Revenue", width: 112, value: (r) => fmtMoney(r.revenue, cur), primary: true },
      { head: "Per visitor", width: 96, value: (r) => fmtMoney(r.per_visitor, cur) },
    ],
    filter: (r) => (fkey && r.key ? [fkey, r.key] : null),
  });

  return card("Revenue by source", `<div class="table-scroll">${table}</div><p class="rev-note">Each sale is credited to the source of the visit it happened in: the channel, referrer, campaign and entry page of that visit's first page. Country and device are the buyer's at the time.</p>`, {
    csvKey: "revenue",
    extra: `<div class="seg-scroll">${seg("rdim", RDIM_TABS, dim)}</div>`,
    slot: "rdim",
  });
}

/** Revenue dimension: only "Revenue by source" fetches and redraws; its switcher keeps its scroll. */
function switchRevenueDim(): void {
  updateCard("rdim", async (current) => {
    const rev = await fetchRevenue();
    if (!current()) return;
    if (!rev.enabled) return void refresh();
    const left = slotOf("rdim")?.querySelector(".seg-scroll")?.scrollLeft ?? 0;
    const el = swapCard("rdim", revenueByCard(rev));
    const strip = el?.querySelector(".seg-scroll");
    if (strip) strip.scrollLeft = left;
    csv.revenue = revenueCsv(rev);
  });
}

function fmtGap(ms: number | null): string {
  if (ms === null) return "";
  const m = ms / 60_000;
  if (m < 1) return `${Math.round(ms / 1000)}s`;
  if (m < 60) return `${Math.round(m)}m`;
  const h = m / 60;
  return h < 48 ? `${h.toFixed(h < 10 ? 1 : 0)}h` : `${Math.round(h / 24)}d`;
}

/** Steps as bars against the first step, with the drop-off and typical time between steps. */
function funnelChart(r: FunnelReport): string {
  const first = r.steps[0]?.visitors ?? 0;
  if (!first) return `<div class="empty">Nobody has started this funnel in this range yet.</div>`;
  const rows = r.steps
    .map((s, i) => {
      const w = first ? (s.visitors / first) * 100 : 0;
      const drop =
        i > 0
          ? `<div class="f-drop"><span>↓ ${fmtPct(s.dropoff)} dropped</span>${s.median_ms !== null ? `<span>typically ${fmtGap(s.median_ms)} later</span>` : ""}</div>`
          : "";
      return `${drop}<div class="f-step"><div class="f-top"><span><span class="num-badge">${i + 1}</span>${esc(s.label)}</span><span><b class="num">${fmtN(s.visitors)}</b> ${s.visitors === 1 ? "visitor" : "visitors"} · ${fmtPct(s.conversion)}</span></div>
        <div class="f-bar"><div style="width:${w.toFixed(1)}%"></div></div></div>`;
    })
    .join("");
  const last = r.steps[r.steps.length - 1]!;
  return `<div class="funnel"><div class="f-sum"><b class="num">${fmtPct1(last.conversion)}</b><span>of visitors who started went all the way through, within ${r.funnel.window_days} ${r.funnel.window_days === 1 ? "day" : "days"}</span></div>${rows}
    ${r.sampled ? `<div class="notice">This range has a lot of activity, so these counts come from a sample and are a lower bound.</div>` : ""}</div>`;
}

/** Show only the value input that matches each rule's type. */
function syncRuleInputs(): void {
  for (const sel of document.querySelectorAll<HTMLSelectElement>("[data-kind-select]")) {
    const holder = sel.nextElementSibling as HTMLElement;
    holder.dataset.kind = sel.value;
  }
}

async function submitForm(form: HTMLFormElement): Promise<void> {
  const err = form.querySelector<HTMLElement>(".form-err")!;
  const f = new FormData(form);
  const id = form.dataset.id || undefined;
  try {
    if (form.dataset.form === "goal") {
      const body = { project: state.project, name: String(f.get("name") ?? ""), ...readRule(form, ""), value_prop: String(f.get("value_prop") ?? "").trim() || null };
      await (id ? send("PATCH", `goals/${id}`, body) : send("POST", "goals", body));
    } else {
      const steps: (Rule & { label: string })[] = [];
      for (let i = 0; form.querySelector(`[name="s${i}label"]`); i++) steps.push({ label: String(f.get(`s${i}label`) ?? ""), ...readRule(form, `s${i}`) });
      const body = { project: state.project, name: String(f.get("name") ?? ""), window_days: Number(f.get("window")), steps };
      const saved = await (id ? send<FunnelDefUI>("PATCH", `funnels/${id}`, body) : send<FunnelDefUI>("POST", "funnels", body));
      funnelId = saved.id;
    }
    editing = null;
    goalPrefill = null;
    void refresh();
  } catch (e) {
    err.textContent = PROBLEMS[(e as Error).message] ?? `Couldn't save: ${(e as Error).message}`;
    err.hidden = false;
  }
}

async function conversionAction(act: string, id: string | undefined): Promise<void> {
  confirmDelete = null;
  if (act !== "step-add" && act !== "step-remove") goalPrefill = null;
  switch (act) {
    case "goal-new": editing = { what: "goal" }; break;
    case "goal-edit": editing = { what: "goal", id }; break;
    case "funnel-new": editing = { what: "funnel" }; funnelStepCount = 3; break;
    case "funnel-edit": editing = { what: "funnel", id }; break;
    case "cancel": editing = null; break;
    case "currency-edit":
      openSettingsTab("site");
      state.site = state.project;
      state.screen = "settings";
      break;
    case "goal-delete": case "funnel-delete": confirmDelete = id ?? null; break;
    case "goal-delete-yes": await send("DELETE", `goals/${id}`); break;
    case "funnel-delete-yes": await send("DELETE", `funnels/${id}`); funnelId = null; break;
    case "step-add": case "step-remove": {
      // Keep what's typed: grow or shrink the editor in place rather than re-rendering.
      const list = document.querySelector<HTMLOListElement>(".steps")!;
      const n = list.children.length;
      if (act === "step-add" && n < 6) {
        const li = document.createElement("li");
        li.innerHTML = `<span class="num-badge">${n + 1}</span><input class="in" name="s${n}label" placeholder="Step label" maxlength="100"><div class="rule">${ruleFields(`s${n}`)}</div>`;
        list.append(li);
      } else if (act === "step-remove" && n > 2) list.lastElementChild!.remove();
      const count = list.children.length;
      document.querySelector<HTMLButtonElement>('[data-act="step-add"]')!.disabled = count >= 6;
      document.querySelector<HTMLButtonElement>('[data-act="step-remove"]')!.disabled = count <= 2;
      syncRuleInputs();
      return;
    }
  }
  void refresh();
}



// ---------- Recent visits (session list and timeline) ----------

let visitorFilter: string | null = null;
let openVisit: string | null = null;

const timeFmt = () =>
  new Intl.DateTimeFormat("en-GB", { timeZone: project().timezone, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const shortVisitor = (v: string) => (v.startsWith("a:") ? `Device ${v.slice(2, 8)}` : `Visitor ${v.slice(2, 8)}`);
const offset = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `+${s}s` : `+${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
};
const EVENT_LABEL: Record<string, string> = {
  whatsapp_click: "Tapped a WhatsApp link",
  phone_click: "Tapped a phone link",
  email_click: "Clicked an email link",
  outbound_click: "Left via a link",
  form_submit: "Sent a form",
  app_opened: "Opened the app",
  app_backgrounded: "Left the app",
  identify: "Signed in",
};

async function renderVisits(gen: number): Promise<void> {
  const host = document.getElementById("visits");
  if (!host || shared) return;
  try {
    const p: Record<string, string> = {};
    if (visitorFilter) p.visitor = visitorFilter;
    const { visits } = await api<{ visits: VisitSummary[] }>("visits", params(p));
    if (gen !== generation) return;
    const fmt = timeFmt();
    const chip = visitorFilter
      ? `<div class="chips" style="margin-bottom:8px"><span class="chip">Only <b>${esc(shortVisitor(visitorFilter))}</b><button data-visitor-clear aria-label="Show all visits">×</button></span></div>`
      : "";
    host.innerHTML =
      chip +
      (visits.length
        ? visits
            .map((v) => {
              const meta = [v.platform !== "web" ? v.platform.toUpperCase() : "", v.source || v.channel, v.country ? countryName(v.country) : "", [cap(v.device), v.browser !== "App" ? v.browser : ""].filter(Boolean).join(" · ")]
                .filter(Boolean)
                .join(" · ");
              const open = openVisit === v.sid;
              return `<div class="visit${open ? " open" : ""}">
                <button class="visit-row" data-visit="${esc(v.sid)}" aria-expanded="${open}">
                  <span class="vt">${fmt.format(v.started)}</span>
                  <span class="vw"><b>${esc(v.user_id ? "Signed-in reader" : shortVisitor(v.visitor))}</b><span>${esc(meta)}</span></span>
                  <span class="vn">${plural(v.views, "view")} · ${fmtDur((v.ended - v.started) / 1000)}</span>
                </button>
                ${open ? `<div class="timeline" id="tl">${skeleton(80)}</div>` : ""}
              </div>`;
            })
            .join("")
        : `<div class="empty">No visits in this range yet.</div>`);
    if (openVisit && visits.some((v) => v.sid === openVisit)) {
      const v = visits.find((x) => x.sid === openVisit)!;
      const { events } = await api<{ events: VisitEvent[] }>("visit", params({ sid: openVisit }));
      if (gen !== generation) return;
      const tl = document.getElementById("tl");
      if (!tl) return;
      tl.innerHTML = `<ol>${events
        .filter((e) => e.type !== "vitals")
        .map((e) => {
          const isView = e.type === "pageview" || e.type === "screen";
          let detail = "";
          try {
            const pr = e.props ? (JSON.parse(e.props) as Record<string, unknown>) : {};
            if (typeof pr.url === "string" && pr.url.startsWith("http")) detail = pr.url;
            else if (typeof pr.slug === "string") detail = pr.slug;
            else if (typeof pr.form_id === "string") detail = `form “${pr.form_id}”`;
          } catch {}
          const what = isView
            ? `<span class="mono">${esc(e.path || e.name)}</span>`
            : `${esc(EVENT_LABEL[e.name] ?? e.name)}${detail ? ` <span class="muted">${esc(detail)}</span>` : ""}`;
          return `<li class="${isView ? "view" : "ev"}"><span class="off">${offset(e.ts - v.started)}</span><span class="dotline"></span><span class="what">${what}</span></li>`;
        })
        .join("")}</ol>${visitorFilter ? "" : `<button class="link" data-visitor="${esc(v.visitor)}">All visits from this ${v.visitor.startsWith("a:") ? "device" : "visitor"}</button>`}`;
    }
  } catch (err) {
    if (gen === generation) host.innerHTML = errorCard(err);
  }
}

// ---------- Returning visitors and retention (retention.ts) ----------

const uptimeOpts = (gen: number) => ({ api, project: state.project, timezone: project().timezone, current: () => gen === generation });
const metricOpts = (gen: number, mobile: boolean) => ({ api, params: params(), mobile, current: () => gen === generation });
const retOpts = (gen: number) => ({
  api, params: params(), current: () => gen === generation, mobile: mq.matches, errorCard, filtered: Object.keys(state.filters).length > 0,
});
onRetentionWeeks(() => void renderRetention(retOpts(generation)));
const summaryOpts = (gen: number): SummaryOpts => ({
  api, params: params(), current: () => gen === generation, admin: me?.role === "admin", filtered: Object.keys(state.filters).length > 0,
});
onSummaryRefresh(() => summaryOpts(generation));

// ---------- Page flow (journeys): where visitors came from and went next ----------

let flowPath: string | null = null;

/** Renders into #flow after the rest of Traffic, so the screen never waits on it. */
async function renderFlow(gen: number, compact: boolean): Promise<void> {
  const host = document.getElementById("flow");
  if (!host) return;
  try {
    const pages = await api<Breakdown>("breakdown", params({ dim: "page", limit: "30" }));
    if (gen !== generation) return;
    if (!pages.rows.length) {
      host.innerHTML = `<div class="empty">No page views in this range yet.</div>`;
      return;
    }
    if (!flowPath || !pages.rows.some((r) => r.key === flowPath)) flowPath = pages.rows[0]!.key;
    const [j, top] = await Promise.all([
      api<Journey>("journeys", params({ path: flowPath })),
      api<Journey>("journeys", params()),
    ]);
    if (gen !== generation || j.page === null || top.page !== null) return;

    const picker = `<label class="${compact ? "m-date" : "project"} flow-pick"><span class="name mono">${esc(flowPath)}</span><span class="caret" aria-hidden="true">▼</span>
      <select class="flow-select" aria-label="Page">${pages.rows.map((r) => `<option value="${esc(r.key)}"${r.key === flowPath ? " selected" : ""}>${esc(r.key)}</option>`).join("")}</select></label>`;
    const left = j.arrivals ? Math.round((j.exits / j.arrivals) * 100) : 0;
    const list = (title: string, rows: { key: string; n: number; special?: boolean }[]) => {
      const max = Math.max(1, ...rows.map((r) => r.n));
      return `<div class="flow-col"><span class="h">${title}</span>${
        rows.length
          ? rows
              .map(
                (r) => `<div class="mbar ${r.special ? "" : "warm"}${r.special ? "" : ` clickable" data-flow="${esc(r.key)}`}"><div class="bar" style="width:${((r.n / max) * 100).toFixed(1)}%"></div>
                  <span class="l ${r.special ? "" : "path"}">${esc(r.key)}</span><span class="v">${fmtN(r.n)}</span></div>`,
              )
              .join("")
          : `<div class="empty">Nothing yet.</div>`
      }</div>`;
    };
    const came = [
      ...(j.entries ? [{ key: "Started their visit here", n: j.entries, special: true }] : []),
      ...j.previous.map((r) => ({ key: r.key, n: r.steps })),
    ].sort((a, b) => b.n - a.n);
    const went = [
      ...(j.exits ? [{ key: "Left the site", n: j.exits, special: true }] : []),
      ...j.next.map((r) => ({ key: r.key, n: r.steps })),
    ].sort((a, b) => b.n - a.n);
    const sources = j.entry_sources.length
      ? `<span class="flow-src">Visits that started here came from ${j.entry_sources.slice(0, 3).map((s) => `${esc(s.key || "(direct)")} (${fmtN(s.steps)})`).join(", ")}.</span>`
      : "";
    const flows = top.flows.length
      ? rankTable(top.flows, {
          head: "Step",
          name: (f) => `<span class="path flow-step">${esc(f.from)} <span class="arrow">→</span> ${esc(f.to)}</span>`,
          bar: (f) => f.steps,
          warm: true,
          cols: [{ head: "Times", width: 72, value: (f) => fmtN(f.steps), primary: true }, { head: "Visits", width: 72, value: (f) => fmtN(f.sessions) }],
        })
      : `<div class="empty">No page-to-page steps recorded in this range yet.</div>`;

    host.innerHTML = `<div class="flow-head">${picker}<span class="flow-sum"><b class="num">${fmtN(j.arrivals)}</b> views in ${plural(j.sessions, "visit")} · <b class="num">${left}%</b> left the site here</span></div>
      ${sources}
      <div class="flow-grid">${list("CAME FROM", came)}${list("WENT NEXT", went)}</div>
      <h3 class="flow-top">Most common steps</h3>${flows}
      ${j.ignored_filters.length ? `<div class="empty">Page flow ignores the ${j.ignored_filters.join(", ")} filter.</div>` : ""}`;
  } catch (err) {
    if (gen === generation) host.innerHTML = errorCard(err);
  }
}

// ---------- mobile (design_handoff_monoblock_analytics_mobile, below 640px) ----------

const mq = matchMedia("(max-width: 640px)");
const RANGE_LABEL: Record<Range, string> = { today: "Today", "7d": "7 days", "30d": "30 days", "90d": "90 days" };
const mseg = (attr: string, items: [string, string][], cur: string, small = false) =>
  `<div class="mseg ${small ? "s" : ""}" role="group">${items.map(([l, v]) => `<button data-${attr}="${v}" aria-pressed="${v === cur}">${l}</button>`).join("")}</div>`;
const mcard = (title: string, body: string, hint = "") =>
  `<section class="card"><div class="card-head"><h2>${title}</h2><span class="spacer"></span>${hint ? `<span class="hint">${hint}</span>` : ""}</div>${body}</section>`;
const mskeleton = (n: number, h: number) => Array.from({ length: n }, () => skeleton(h)).join("");

function mProject(): string {
  const p = project();
  return `<label class="m-project"><span class="badge">${esc((p.name.replace(/[^a-z0-9]/gi, "")[0] ?? "M").toUpperCase())}</span><span class="name">${esc(p.name)}</span><span class="caret" aria-hidden="true">▼</span>
    <select class="m-project-select" aria-label="Project">${projectOptions(projects, state.project)}</select></label>`;
}
const mChips = () => (Object.keys(state.filters).length ? `<div class="chips">${$("chips").innerHTML}</div>` : "");

/** A 44px bar row: the bar is the value relative to the top row. */
function mBars<R>(rows: R[], o: { name: (r: R) => string; value: (r: R) => string; bar: (r: R) => number; warm?: boolean; filter?: (r: R) => [FilterKey, string] | null }): string {
  if (!rows.length) return `<div class="empty">No data for this range yet.</div>`;
  const max = Math.max(1, ...rows.map(o.bar));
  return rows
    .map((r) => {
      const f = o.filter?.(r);
      const click = f && !state.filters[f[0]] ? ` clickable" data-filter="${f[0]}" data-value="${esc(f[1])}` : "";
      return `<div class="mbar ${o.warm ? "warm" : ""}${click}"><div class="bar" style="width:${((o.bar(r) / max) * 100).toFixed(1)}%"></div>${o.name(r)}<span class="v">${o.value(r)}</span></div>`;
    })
    .join("");
}

async function mOverview(gen: number): Promise<void> {
  const head = `<div class="m-row">${mProject()}<span class="m-live" id="m-live" data-tab="realtime" role="button" tabindex="0" aria-label="Open Realtime" hidden><span class="dot"><i></i><i></i></span><span id="m-live-text"></span></span><button class="btn m-share no-share" data-share-open>Share</button></div>${mChips()}
    ${mseg("range", [["Today", "today"], ["7d", "7d"], ["30d", "30d"], ["90d", "90d"]], state.range)}`;
  screen.innerHTML = `${head}<div class="m-kpis">${mskeleton(6, 76)}</div>${mcard("…", skeleton(190))}`;
  const extra: Record<string, string> = state.range === "today" ? { chart_from: shift(project().today, -1) } : {};
  const [o, notes] = await Promise.all([api<Overview>("overview", params(extra)), fetchNotes(state.project, extra.chart_from ?? params().from!, params().to!)]);
  if (gen !== generation) return;

  const M = metricDef();
  const first = o.daily[0]?.day ?? o.range.from;
  const chTotal = o.channels.reduce((s, c) => s + c.visitors, 0);
  const note = o.source === "rollup" ? `<div class="notice">Older than 90 days: read from daily summaries, so visitors are summed per day and visit length isn't available.</div>` : "";
  const goals = o.goals.length
    ? `<div class="m-goals">${o.goals
        .map(
          (g) => `<div class="m-goal"><div class="t"><span>${esc(g.name)}</span>${deltaPill(g.rate, g.prev_rate)}</div>
        <div class="r"><b class="num">${fmtPct1(g.rate)}</b><span>${plural(g.completions, "completion")} · ${plural(g.visitors, "visitor")}</span></div>
        <div class="progress"><div style="width:${Math.min(100, (g.rate ?? 0) * 1000).toFixed(1)}%"></div></div></div>`,
        )
        .join("")}</div>`
    : `<div class="empty">No goals set up for this project yet.</div>`;

  screen.innerHTML = `${head}${orgNote(project())}${note}${summaryCard(!isOrg(project()))}<div class="m-kpis" id="kpis" role="group" aria-label="Chart metric">${kpisHtml(o, true)}</div>
  <section class="card">
    <div class="m-chart-head"><h2 id="chart-title">${M.label} per day</h2><span>${dayLabel(first)} – ${dayLabel(o.range.to)}</span></div>
    <div class="m-legend"><span><i class="cur"></i>This period</span>${o.previous_daily.length ? `<span><i class="prev"></i>Previous</span>` : ""}</div>
    <div id="chart"></div>
    <div id="notes" class="notes"></div>
  </section>
  ${metricCardsSlot()}
  ${mcard("Channels", mBars(o.channels, {
    name: (c) => `<span class="l">${esc(c.key || "(none)")}<span class="sub">${chTotal ? `${Math.round((c.visitors / chTotal) * 100)}%` : ""}</span></span>`,
    value: (c) => fmtN(c.visitors), bar: (c) => c.visitors, filter: (c) => ["channel", c.key],
  }), "Visitors")}
  ${mcard("Top pages", mBars(o.pages, {
    name: (p) => `<span class="l path">${esc(p.key || "(none)")}</span>`,
    value: (p) => fmtN(p.pageviews), bar: (p) => p.pageviews, warm: true, filter: (p) => ["page", p.key],
  }), "Views")}
  ${state.filters.page ? `<div id="page-detail"></div>` : ""}
    ${returningCard(true)}
  ${isOrg(project()) ? "" : uptimeCard()}
  ${mcard("Goals", `${goals}<button class="btn" style="width:100%;margin-top:12px" data-tab="conversions">Goals & funnels</button>`)}`;

  settled = gen;
  overviewChart(o, notes, gen, true);
  void renderMetricCards(metricOpts(gen, true));
  void renderReturning(retOpts(gen));
  void renderUptime(uptimeOpts(gen));
  void renderPageDetail(gen);
  void renderSummary(summaryOpts(gen));
  if (o.live !== null) {
    $("m-live").hidden = false;
    $("m-live-text").textContent = `${fmtN(o.live)} live`;
  }
}

/** Project switcher, date range and filter chips: the top of mobile Traffic and Conversions. */
const mHead = () => `<div class="m-row">${mProject()}<label class="m-date">${RANGE_LABEL[state.range]}<span class="caret" aria-hidden="true">▼</span>
    <select class="m-range-select" aria-label="Date range">${(Object.keys(RANGE_LABEL) as Range[]).map((r) => `<option value="${r}"${r === state.range ? " selected" : ""}>${RANGE_LABEL[r]}</option>`).join("")}</select></label></div>${mChips()}`;

async function mTraffic(gen: number): Promise<void> {
  const head = mHead();
  screen.innerHTML = `${head}${mcard("Sources", mskeleton(4, 60))}${mcard("Countries", mskeleton(4, 44))}`;
  const [src, geo, dev, br, ee, lang] = await Promise.all([
    fetchSources(),
    api<Breakdown>("breakdown", params({ dim: "country", limit: "8" })),
    api<Breakdown>("breakdown", params({ dim: "device", limit: "10" })),
    api<Breakdown>("breakdown", params({ dim: "browser", limit: "20" })),
    api<EntryExit>("entry_exit", params()),
    api<Breakdown>("breakdown", params({ dim: "language", limit: "8" })),
  ]);
  if (gen !== generation) return;
  mEntryExit = ee;

  const devRows = dev.rows.filter((r) => r.visitors > 0).sort((a, b) => ["desktop", "mobile", "tablet"].indexOf(a.key) - ["desktop", "mobile", "tablet"].indexOf(b.key));
  const devTotal = devRows.reduce((s, r) => s + r.visitors, 0) || 1;
  const brTotal = br.rows.reduce((s, r) => s + r.visitors, 0) || 1;
  const brItems = [...br.rows.slice(0, 4).map((r) => [r.key || "Other", r.visitors] as const)];
  const brOther = br.rows.slice(4).reduce((s, r) => s + r.visitors, 0);
  if (brOther) brItems.push(["Other", brOther]);
  const devices = devRows.length
    ? `<div class="m-dev"><div class="stackbar">${devRows.map((d) => `<div style="width:${(d.visitors / devTotal) * 100}%;background:${DEVICE_COLORS[d.key] ?? "#CBD2DF"}"></div>`).join("")}</div>
      <div class="legend3">${devRows.map((d) => `<div><span class="k"><i style="background:${DEVICE_COLORS[d.key] ?? "#CBD2DF"}"></i>${esc(label("device", d.key))}</span><b class="num">${Math.round((d.visitors / devTotal) * 100)}%</b></div>`).join("")}</div>
      <div class="browsers">${brItems.map(([n, v]) => {
        const pct = `${Math.round((v / brTotal) * 100)}%`;
        return `<div class="br"><span>${esc(n)}</span><div class="t"><div style="width:${pct}"></div></div><span>${pct}</span></div>`;
      }).join("")}</div></div>`
    : `<div class="empty">No data for this range yet.</div>`;

  screen.innerHTML = `${head}
  ${orgSplitCard(project())}
  ${mSourcesCard(src)}
  ${mcard("Countries", mBars(geo.rows, {
    name: (c) => `<span class="l center"><span class="iso">${esc(c.key || "??")}</span>${esc(countryName(c.key))}</span>`,
    value: (c) => fmtN(c.visitors), bar: (c) => c.visitors, filter: (c) => ["country", c.key],
  }), "Visitors")}
  ${mcard("Devices", devices)}
  ${mcard("Languages", mBars(lang.rows, {
    name: (l) => `<span class="l">${esc(languageName(l.key))}</span>`,
    value: (l) => fmtN(l.visitors), bar: (l) => l.visitors, filter: (l) => (l.key ? ["language", l.key] : null),
  }), "Visitors")}
  ${mPagesCard(ee)}
  ${retentionCard(true)}
  <div id="gsc" hidden></div>
  ${mcard("Page flow", `<div id="flow">${skeleton(200)}</div>`)}
  ${mcard("Recent visits", `<div id="visits">${skeleton(200)}</div>`)}`;
  settled = gen;
  void renderFlow(gen, true);
  void renderRetention(retOpts(gen));
  void renderSearch(document.getElementById("gsc"), { project: project(), params: params(), mobile: true, current: () => gen === generation });
  void orgSplit(document.getElementById("org-split"), params(), () => gen !== generation);
  void renderVisits(gen);
}
let mPage: "entry" | "exit" = "entry";
/** Mobile Traffic's entry and exit pages, so its Entry/Exit toggle redraws without a fetch. */
let mEntryExit: EntryExit | null = null;

/** Mobile Sources, with its Channels / Referrers / Campaigns toggle. */
function mSourcesCard(src: Sources): string {
  const topV = Math.max(1, ...src.rows.map((s) => s.visitors));
  const meta = (s: Sources["rows"][number]) =>
    [
      plural(s.sessions, "session"),
      s.bounce_rate !== null ? `${fmtPct(s.bounce_rate)} bounce` : "",
      s.conv_rate !== null ? `${fmtPct1(s.conv_rate)} conv.` : "",
      src.currency && s.revenue !== null ? `${fmtMoney(s.revenue, src.currency)} revenue` : "",
    ].filter(Boolean).join(" · ");
  const sources = src.rows.length
    ? src.rows
        .map((s) => {
          const click = !state.filters[state.src] ? ` clickable" data-filter="${state.src}" data-value="${esc(s.key)}` : "";
          return `<div class="m-src${click}"><div class="a"><span>${esc(s.key || "(none)")}${s.sub ? `<small>${esc(s.sub)}</small>` : ""}</span><b>${fmtN(s.visitors)}</b></div>
          <div class="t"><div style="width:${((s.visitors / topV) * 100).toFixed(1)}%"></div></div><span class="m">${meta(s)}</span></div>`;
        })
        .join("")
    : `<div class="empty">No data for this range yet.</div>`;
  return `<section class="card" data-card="src"><div class="card-head"><h2>Sources</h2></div>${mseg("src", SRC_TABS, state.src, true)}${sources}</section>`;
}

/** Mobile entry or exit pages, with the toggle between them. */
function mPagesCard(ee: EntryExit): string {
  const peRows = mPage === "entry"
    ? ee.entry.map((p) => ({ key: p.key, a: fmtN(p.sessions), b: fmtPct(p.bounce_rate) }))
    : ee.exit.map((p) => ({ key: p.key, a: fmtN(p.exits), b: fmtPct(p.exit_rate) }));
  const pe = peRows.length
    ? peRows.map((p) => `<div class="m-pe"><span class="p" title="${esc(p.key)}">${esc(p.key || "(none)")}</span><b>${p.a}</b><span>${p.b}</span></div>`).join("")
    : `<div class="empty">No data for this range yet.</div>`;
  return `<section class="card" data-card="pe">${mseg("pe", [["Entry pages", "entry"], ["Exit pages", "exit"]], mPage, true)}${pe}</section>`;
}

/** Sources toggle (desktop and mobile): only the Sources card fetches and redraws. */
function switchSources(): void {
  const mobile = mq.matches;
  updateCard("src", async (current) => {
    const src = await fetchSources();
    if (!current()) return;
    swapCard("src", mobile ? mSourcesCard(src) : sourcesCard(src));
    csv.sources = sourcesCsv(src);
  });
}

/** Countries / Regions / Cities / Map: Traffic's place card, or the page-detail one on Overview. */
function switchGeo(): void {
  if (slotOf("page-geo"))
    return updateCard("page-geo", async (current) => {
      const geo = await fetchPageGeo();
      if (current()) swapCard("page-geo", pageGeoCard(geo));
    });
  updateCard("geo", async (current) => {
    const geo = await fetchGeo();
    if (!current()) return;
    swapCard("geo", geoCard(geo));
    $("maptip").hidden = true;
    await geoAfter(geo);
  });
}

async function mPerformance(gen: number): Promise<void> {
  const devSeg = mseg("device", [["All", "all"], ["Mobile", "mobile"], ["Desktop", "desktop"]], state.device);
  screen.innerHTML = `${mPerfHead(null)}${devSeg}${mPerfBody(null)}`;
  const v = await fetchVitals();
  if (gen !== generation) return;
  screen.innerHTML = `${mPerfHead(v)}${devSeg}
    ${mPerfBody(v)}`;
  settled = gen;
}

const mPerfHead = (v: Vitals | null) =>
  `<div class="m-perf-head"><h1>Core Web Vitals</h1><span>${v ? `p75 from ${plural(vitalLoads(v), "page load")} · ${RANGE_LABEL[state.range]}` : "&nbsp;"}</span></div>`;

/** Mobile Performance under the device toggle; `null` draws it loading. */
function mPerfBody(v: Vitals | null): string {
  if (!v) return `<div class="slot" data-card="perf"><div class="m-vitals">${mskeleton(4, 150)}</div></div>`;
  const loads = vitalLoads(v);
  const pct = (f?: number) => `${Math.round((f ?? 0) * 100)}%`;
  const DOT = { good: "var(--good)", ni: "var(--warn)", poor: "var(--bad)" };
  const SHORT: Record<VitalKey, string> = { lcp: "≤ 2.5s", inp: "≤ 200ms", cls: "≤ 0.10", ttfb: "≤ 0.8s", fcp: "≤ 1.8s" };
  const MRATING = { good: "Good", ni: "Needs work", poor: "Poor" } as const;
  const cards = VITALS.map((x) => {
    const m = v.overall[x.key];
    const r = m ? rateVital(x.key, m.p75) : null;
    return `<div class="m-vital"><div class="t"><span>${x.abbr}</span><span class="rd" style="background:${r ? DOT[r] : "var(--divider)"}"></span></div>
      <span class="v num">${m ? fmtVital(x.key, m.p75) : "–"}</span>
      <span class="pill ${r ?? "none"}">${r ? MRATING[r] : "No data"}</span>
      ${m && m.good !== undefined ? `<div class="dist"><div style="width:${pct(m.good)};background:var(--good)"></div><div style="width:${pct(m.ni)};background:var(--warn)"></div><div style="width:${pct(m.poor)};background:var(--bad)"></div></div>` : ""}
      <span class="f">${m && m.good !== undefined ? `${pct(m.good)} good · ` : ""}${SHORT[x.key]}</span>${lowSample(m?.samples)}</div>`;
  }).join("");
  const pages = v.rows.length
    ? v.rows
        .map((p) => {
          const n = Math.max(0, ...VITALS.map((x) => p[x.key]?.samples ?? 0));
          return `<div class="m-page"><div class="a"><span title="${esc(p.key)}">${esc(p.key)}</span><span>${plural(n, "load")}</span></div><div class="m-cells">${VITALS.map((x) => {
            const m = p[x.key];
            const r = m ? rateVital(x.key, m.p75) : null;
            return `<div class="m-cell ${r ?? ""}"><span>${x.abbr}</span><b>${m ? fmtVital(x.key, m.p75) : "–"}${r ? `<span class="sr"> (${MRATING[r]})</span>` : ""}</b></div>`;
          }).join("")}</div></div>`;
        })
        .join("")
    : `<div class="empty">No page loads measured yet in this range.</div>`;

  return `<div class="slot" data-card="perf">${lowSampleNotice(loads)}
    <div class="m-vitals">${cards}</div>
    <section class="card"><h2 style="margin:0 0 4px;font-size:15px;font-weight:600">Pages</h2><span class="m-pages-sub">Sorted by loads. Dot colour shows the rating.</span>${pages}</section></div>`;
}

// ---------- frame ----------

function renderChips(): void {
  $("chips").innerHTML = Object.entries(state.filters)
    .map(([k, v]) => `<span class="chip">${k.replace("_", " ").replace("utm ", "UTM ")} is <b>${esc(label(k, v))}</b><button data-unfilter="${k}" aria-label="Remove filter">×</button></span>`)
    .join("");
}

function setFilter(k: FilterKey, v: string): void {
  state.filters[k] = v;
  void refresh();
}

async function refresh(): Promise<void> {
  const gen = ++generation;
  writeUrl();
  renderChips();
  for (const b of document.querySelectorAll<HTMLElement>("[data-tab]")) b.setAttribute("aria-selected", String(b.dataset.tab === state.screen));
  document.body.dataset.screen = state.screen;
  for (const b of document.querySelectorAll<HTMLElement>("[data-range]")) b.setAttribute("aria-pressed", String(b.dataset.range === state.range));
  if (PING) return refreshPing(gen);
  const p = project();
  $("project-name").textContent = p.name;
  $("project-badge").textContent = (p.name.replace(/[^a-z0-9]/gi, "")[0] ?? "M").toUpperCase();
  $("maptip").hidden = true;
  stopRealtime();
  $("range").hidden = state.screen === "realtime";
  try {
    // The Inbox is for admins; anyone else (or an old link) lands on Home.
    if (state.screen === "inbox" && me?.role !== "admin") state.screen = "home";
    if (state.screen === "inbox") {
      const openId = inboxOpen;
      inboxOpen = null;
      await renderInbox({
        el: screen,
        mobile: mq.matches,
        current: () => gen === generation && state.screen === "inbox",
        openId,
        onSettings: () => {
          openSettingsTab("notifications");
          state.screen = "settings";
          void refresh();
        },
      });
    } else if (state.screen === "home")
      await renderHome({ el: screen, range: state.range, mobile: mq.matches, current: () => gen === generation, errorCard, admin: me?.role === "admin", onCreated: reloadProjects });
    else if (isOrg(p) && (state.screen === "performance" || state.screen === "conversions" || state.screen === "errors" || state.screen === "realtime"))
      screen.innerHTML = `${mq.matches ? `<div class="m-row">${mProject()}</div>` : ""}${orgOnlyNotice(p, state.screen)}`;
    else if (state.screen === "settings")
      await renderSettingsPage({
        el: screen,
        mobile: mq.matches,
        current: () => gen === generation,
        me: me!,
        sites: projects.filter((x) => !isOrg(x)).map(({ id, name }) => ({ id, name })),
        site: state.site || state.project,
        onSite: (id) => {
          state.site = id;
          writeUrl();
        },
        onSiteChanged: (c) => void siteChanged(c),
      });
    else if (state.screen === "errors") await renderErrors({ el: screen, params: params(), current: () => gen === generation, mobile: mq.matches });
    else if (state.screen === "realtime")
      await startRealtime(screen, {
        api,
        projectId: state.project,
        mobile: mq.matches,
        current: () => gen === generation && state.screen === "realtime",
        mobileHead: mq.matches ? `<div class="m-row">${mProject()}</div>` : "",
        errorCard,
      });
    else if (mq.matches) {
      if (state.screen === "conversions") await renderConversions(gen);
      else if (state.screen === "traffic") await mTraffic(gen);
      else if (state.screen === "performance") await mPerformance(gen);
      else await mOverview(gen);
    } else if (state.screen === "conversions") await renderConversions(gen);
    else if (state.screen === "traffic") await renderTraffic(gen);
    else if (state.screen === "performance") await renderPerformance(gen);
    else await renderOverview(gen);
  } catch (err) {
    if (gen === generation) screen.innerHTML = errorCard(err);
  }
}

/** Ping on ping.monoblock.ae: Ping itself, or its settings (Home and the analytics don't exist here). */
async function refreshPing(gen: number): Promise<void> {
  if (state.screen !== "settings" && state.screen !== "inbox") {
    state.screen = "inbox";
    writeUrl();
    for (const b of document.querySelectorAll<HTMLElement>("[data-tab]")) b.setAttribute("aria-selected", String(b.dataset.tab === state.screen));
    document.body.dataset.screen = state.screen;
  }
  try {
    if (me?.role !== "admin") {
      screen.innerHTML = `<div class="notice">Ping is for MonoLog admins. Your sites' analytics are on <a href="https://log.monoblock.ae/">MonoLog</a>.</div>`;
      return;
    }
    if (state.screen === "settings") {
      await renderSettingsPage({ el: screen, mobile: mq.matches, current: () => gen === generation, me, sites: [], site: "", ping: true });
      return;
    }
    const openId = inboxOpen;
    inboxOpen = null;
    await renderInbox({
      el: screen,
      mobile: mq.matches,
      current: () => gen === generation && state.screen === "inbox",
      openId,
      onSettings: () => {
        openSettingsTab("notifications");
        state.screen = "settings";
        void refresh();
      },
    });
  } catch (err) {
    if (gen === generation) screen.innerHTML = errorCard(err);
  }
}

async function refreshLive(): Promise<void> {
  try {
    const o = await api<{ live: number | null }>("live", { project: state.project });
    $("live").hidden = o.live === null;
    if (o.live !== null) $("live-text").textContent = `${fmtN(o.live)} ${o.live === 1 ? "visitor" : "visitors"} online now`;
    const ml = document.getElementById("m-live");
    if (ml && o.live !== null) {
      ml.hidden = false;
      $("m-live-text").textContent = `${fmtN(o.live)} live`;
    }
  } catch {}
}

document.addEventListener("click", (ev) => {
  const t = ev.target as HTMLElement;
  const on = <K extends string>(attr: string) => t.closest<HTMLElement>(`[data-${attr}]`)?.getAttribute(`data-${attr}`) as K | undefined;
  // An app's error count on Home opens that app's Errors rather than its Overview.
  const appErrors = t.closest<HTMLElement>("[data-home-errors]");
  if (appErrors) return void ((state.screen = "errors"), switchProject(appErrors.dataset.homeErrors!));
  const site = (ev.target as HTMLElement).closest<HTMLElement>("[data-home-site]");
  if (site) return void ((state.screen = "overview"), switchProject(site.dataset.homeSite!));
  const tab = on<Screen>("tab");
  // A shared link only reads the public report endpoints, so the staff-only screens stay closed.
  if (tab) return void (shared && SHARED_HIDDEN.has(tab) ? 0 : ((state.screen = tab), (state.site = ""), refresh()));
  const range = on<Range>("range");
  if (range) return void ((state.range = range), refresh());
  // In-card toggles redraw only their own card (see updateCard); the rest of the screen stays put.
  const hit = (attr: string) => t.closest<HTMLElement>(`[data-${attr}]`);
  const metric = on<Metric>("metric");
  if (metric) return switchMetric(metric);
  const src = on<SourceTab>("src");
  if (src) return void ((state.src = src), press(hit("src")), switchSources());
  const geo = on<GeoTab>("geo");
  if (geo) return void ((state.geo = geo), press(hit("geo")), switchGeo());
  const rdim = on<RevenueDimension>("rdim");
  if (rdim) return void ((state.rdim = rdim), press(hit("rdim")), switchRevenueDim());
  const device = on<Device>("device");
  if (device) return void ((state.device = device), press(hit("device")), switchDevice());
  const evGoal = t.closest<HTMLElement>("[data-ev-goal]");
  if (evGoal) return makeEventGoal(evGoal.dataset.evGoal!);
  const evRow = t.closest<HTMLElement>("[data-event]");
  if (evRow) return toggleEvent(evRow.dataset.event!);
  const prSort = t.closest<HTMLElement>("[data-prod-sort]");
  if (prSort) return sortProducts(prSort.dataset.prodSort as ProductSort);
  if (t.closest("[data-prod-more]")) return void ((productsAll = true), redrawProducts());
  // Links inside an open product open; its filter rows (below) filter the dashboard.
  const prRow = t.closest<HTMLElement>("[data-product]");
  if (prRow && !t.closest("a")) return toggleProduct(prRow.dataset.product!);
  const act = t.closest<HTMLElement>("[data-act]");
  if (act) return void conversionAction(act.dataset.act!, act.dataset.id).catch((e) => alertCard(e));
  const fun = on<string>("funnel");
  if (fun) return void (press(hit("funnel")), switchFunnel(fun));
  const vis = t.closest<HTMLElement>("[data-visit]");
  if (vis) return void ((openVisit = openVisit === vis.dataset.visit ? null : vis.dataset.visit!), renderVisits(generation));
  const who = t.closest<HTMLElement>("[data-visitor]");
  if (who) return void ((visitorFilter = who.dataset.visitor!), (openVisit = null), renderVisits(generation));
  if (t.closest("[data-visitor-clear]")) return void ((visitorFilter = null), renderVisits(generation));
  const fl = on<string>("flow");
  if (fl !== undefined && t.closest("[data-flow]")) return void ((flowPath = fl), renderFlow(generation, mq.matches));
  const pe = on<"entry" | "exit">("pe");
  if (pe) return void ((mPage = pe), mEntryExit && slotOf("pe") && settled === generation ? swapCard("pe", mPagesCard(mEntryExit)) : refresh());
  const un = on<FilterKey>("unfilter");
  if (un) return void (delete state.filters[un], refresh());
  const row = t.closest<HTMLElement>("[data-filter]");
  if (row) return setFilter(row.dataset.filter as FilterKey, row.dataset.value ?? "");
  const c = on<string>("csv");
  if (c) downloadCsv(c);
});

// The live pills open Realtime; they are role="button" spans, so give them the keyboard too.
document.addEventListener("keydown", (ev) => {
  const t = ev.target as HTMLElement;
  if ((ev.key === "Enter" || ev.key === " ") && t.matches('[role="button"][data-tab], [role="button"][data-event], [role="button"][data-product]')) {
    ev.preventDefault();
    t.click();
  }
});

/** After Home adds a site or app: the project list again, so its row (and the menu) can open it. */
async function reloadProjects(): Promise<void> {
  projects = await api<ProjectInfo[]>("projects");
  const sel = $("project") as HTMLSelectElement;
  sel.innerHTML = projectOptions(projects, state.project);
  sel.value = state.project;
}

/**
 * Settings → Site settings saved, deleted or restored a site: the project list again (toolbar,
 * menus; Home reads its own list each time). A save keeps the page as it is; a delete or restore
 * redraws Settings, moving off a deleted site.
 */
async function siteChanged(c: { saved?: { id: string }; deleted?: string; restored?: string }): Promise<void> {
  const list = await api<ProjectInfo[]>("projects");
  if (c.deleted && state.project === c.deleted) state.project = list.find((x) => !isOrg(x))?.id ?? list[0]?.id ?? "";
  if (c.deleted && state.site === c.deleted) state.site = "";
  if (c.restored) state.site = c.restored;
  projects = list;
  const sel = $("project") as HTMLSelectElement;
  sel.innerHTML = projectOptions(projects, state.project);
  sel.value = state.project;
  if (c.saved) {
    const p = project();
    $("project-name").textContent = p.name;
    $("project-badge").textContent = (p.name.replace(/[^a-z0-9]/gi, "")[0] ?? "M").toUpperCase();
    return;
  }
  void refresh();
}

function switchProject(id: string): void {
  state.project = id;
  state.filters = {};
  void refresh();
  void refreshLive();
}
$("project").addEventListener("change", (e) => switchProject((e.target as HTMLSelectElement).value));
onOrgPick(switchProject);
// The mobile switcher and date dropdown are re-rendered with each screen, so listen by delegation.
document.addEventListener("submit", (e) => {
  const form = (e.target as HTMLElement).closest<HTMLFormElement>("form[data-form]");
  if (!form) return;
  e.preventDefault();
  void submitForm(form);
});
document.addEventListener("input", (e) => {
  const t = e.target as HTMLElement;
  if (t.matches("[data-ev-search]")) searchEvents((t as HTMLInputElement).value);
  if (t.matches("[data-prod-search]")) return void ((productQuery = (t as HTMLInputElement).value), redrawProducts());
});
document.addEventListener("change", (e) => {
  const t = e.target as HTMLElement;
  if (t.matches("[data-kind-select]")) return syncRuleInputs();
  if (t.matches("[data-prod-brand]")) return void ((productBrand = (t as HTMLSelectElement).value), redrawProducts());
  if (t.matches("[data-prod-sort-select]")) {
    const key = (t as HTMLSelectElement).value as ProductSort;
    return void ((productSort = { key, asc: key === "name" }), redrawProducts());
  }
  if (t.matches(".flow-select")) {
    flowPath = (t as HTMLSelectElement).value;
    return void renderFlow(generation, mq.matches);
  }
  if (t.matches(".m-project-select")) switchProject((t as HTMLSelectElement).value);
  else if (t.matches(".m-range-select")) {
    state.range = (t as HTMLSelectElement).value as Range;
    void refresh();
  }
});
// Crossing the mobile breakpoint swaps layouts.
mq.addEventListener("change", () => void refresh());

// pointerleave doesn't fire when the page scrolls out from under the cursor.
addEventListener(
  "scroll",
  () => {
    $("maptip").hidden = true;
    for (const el of document.querySelectorAll<HTMLElement>(".chart .guide, .chart .tip, .chart .hdot:not(.fixed)")) el.hidden = true;
  },
  { passive: true, capture: true },
);

// ---------- boot ----------

/** The signed-in account, from /api/me; set before the first render. */
let me: Me | null = null;

/** Ping (admins): the tabs, their unread badge, the service worker for push, and taps on pushes. */
function startPing(): void {
  for (const b of document.querySelectorAll<HTMLElement>("[data-inbox-tab]")) b.hidden = false;
  document.body.classList.add("has-inbox");
  startInboxBadge();
  warmPush();
  // A tapped push notification while the app is open: show that notification here.
  navigator.serviceWorker?.addEventListener("message", (e) => {
    const m = e.data as { type?: string; id?: string } | null;
    if (m?.type !== "monolog:open-notification") return;
    inboxOpen = m.id ?? null;
    state.screen = "inbox";
    void refresh();
  });
}

/** ping.monoblock.ae: no projects, no analytics; just who is signed in, then Ping. */
async function bootPing(): Promise<void> {
  try {
    const who = await api<Me>("me");
    me = who;
    $("who").textContent = who.email;
    $("avatar").textContent = (who.email[0] ?? "?").toUpperCase();
    initAccount(who);
    if (who.role === "admin") {
      startPing();
      for (const b of document.querySelectorAll<HTMLElement>("[data-ping-tab]")) b.hidden = false;
    }
    await refresh();
  } catch (err) {
    screen.innerHTML = errorCard(err);
  }
}

if (PING) void bootPing();
else (async () => {
  try {
    const [who, list] = await Promise.all([api<Me & { label?: string }>("me"), api<ProjectInfo[]>("projects")]);
    me = who;
    projects = list;
    applySharedMode(who.label);
    if (shared && SHARED_HIDDEN.has(state.screen)) state.screen = "overview";
    initShare(project);
    $("who").textContent = who.email;
    $("avatar").textContent = (who.email[0] ?? "?").toUpperCase();
    initAccount(who);
    // Ping (admins): the tabs, their unread badge, and the service worker for push.
    if (who.role === "admin" && !shared) startPing();
    if (!projects.length) {
      screen.innerHTML = `<div class="notice">No projects yet.</div>`;
      return;
    }
    if (!projects.some((p) => p.id === state.project)) state.project = projects[0]!.id;
    const sel = $("project") as HTMLSelectElement;
    sel.innerHTML = projectOptions(projects, state.project);
    sel.value = state.project;
    await refresh();
    void refreshLive();
    setInterval(() => !document.hidden && void refreshLive(), 60_000);
  } catch (err) {
    screen.innerHTML = errorCard(err);
  }
})();
